import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import {
  portalColaboradorService,
} from '../services/portal-colaborador.service';
import { getApiHostURL } from '../utils/apiConfig';
import {
  agendamentosService,
  type AutoagendamentoData,
  type EstadoClinica,
  type ClinicaAutoagendamento,
} from '../services/agendamentos.service';
import type {
  ColaboradorPortalPerfilContract,
  ColaboradorExameHistoricoContract,
  ConsultaDenunciaContract,
  CriarDenunciaAnonimaPayloadContract,
} from '@sstexames/shared-types';
import {
  Calendar,
  MapPin,
  Building2,
  CheckCircle2,
  AlertCircle,
  FileText,
  ShieldCheck,
  Stethoscope,
  Download,
  Search,
  Lock,
  ShieldAlert,
  ArrowRight,
  LogOut,
  Clock,
  Copy,
  ChevronDown,
  ChevronLeft,
  User,
  ExternalLink,
  Heart,
} from 'lucide-react';
import StateSelect from '../components/StateSelect';
import SearchableSelect from '../components/SearchableSelect';
import FormattedInput from '../components/FormattedInput';
import FormField from '../components/FormField';
import { copyToClipboard } from '../utils/clipboard';
import { customerCipaService } from '../services/cipa.service';
import CipaColaboradorTab from './PortalColaborador/CipaColaboradorTab';
import MinhaSaudeColaboradorTab from './PortalColaborador/MinhaSaudeColaboradorTab';

export default function PortalColaborador() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token: tokenParam } = useParams<{ token?: string }>();
  const tokenFromUrl = searchParams.get('token') || tokenParam || null;

  const isEmbedded = useMemo(() => {
    const ref = searchParams.get('ref');
    return ref === 'embeded' || ref === 'embedded';
  }, [searchParams]);

  const isMobileDevice = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(navigator.userAgent);
  }, []);

  // Modo de visualização: guias do portal
  const [activeTab, setActiveTab] = useState<'agendamento' | 'exames' | 'minha_saude' | 'denuncia' | 'cipa'>(
    tokenFromUrl ? 'agendamento' : 'exames',
  );

  // Se embutido (ex: WhatsApp ref=embeded), travar na aba de agendamento sem navegação
  useEffect(() => {
    if (isEmbedded) {
      setActiveTab('agendamento');
    }
  }, [isEmbedded]);

  // Estados de navegação do Wizard Mobile
  const [mobileWizardStep, setMobileWizardStep] = useState<1 | 2 | 3>(1);
  const [mobileDetailsOpen, setMobileDetailsOpen] = useState(false);
  const [mobileDenunciaInfoOpen, setMobileDenunciaInfoOpen] = useState(false);

  // Auto-scroll para colocar em destaque a etapa ativa no mobile
  useEffect(() => {
    if (mobileWizardStep === 2 || mobileWizardStep === 3) {
      const timer = setTimeout(() => {
        const stepElement = document.getElementById(
          mobileWizardStep === 2 ? 'mobile-wizard-step-2' : 'mobile-wizard-step-3'
        );
        if (stepElement) {
          stepElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 60);
      return () => clearTimeout(timer);
    } else if (mobileWizardStep === 1) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [mobileWizardStep]);

  // Estados de identificação / Perfil do Colaborador
  const [cpfInput, setCpfInput] = useState('');
  const [dataNascimentoInput, setDataNascimentoInput] = useState('');
  const [identifying, setIdentifying] = useState(false);
  const [identifyError, setIdentifyError] = useState<string | null>(null);
  const [perfil, setPerfil] = useState<ColaboradorPortalPerfilContract | null>(null);
  const [empresaHasCipa, setEmpresaHasCipa] = useState<boolean>(false);
  const [empresaHasSaude, setEmpresaHasSaude] = useState<boolean>(false);

  // Validação dinâmica se a empresa do colaborador possui os módulos CIPA e Saúde Corporativa contratados
  useEffect(() => {
    let isMounted = true;
    if (perfil?.empresa?.id) {
      customerCipaService
        .checkEmpresaHasCipa(perfil.empresa.id)
        .then((hasCipa) => {
          if (isMounted) setEmpresaHasCipa(hasCipa);
        })
        .catch(() => {
          if (isMounted) setEmpresaHasCipa(false);
        });

      portalColaboradorService
        .checkEmpresaHasSaude(perfil.empresa.id)
        .then((hasSaude) => {
          if (isMounted) setEmpresaHasSaude(hasSaude);
        })
        .catch(() => {
          if (isMounted) setEmpresaHasSaude(false);
        });
    } else {
      setEmpresaHasCipa(false);
      setEmpresaHasSaude(false);
    }
    return () => {
      isMounted = false;
    };
  }, [perfil?.empresa?.id]);

  // Se o colaborador estava na aba cipa ou minha_saude e o respectivo módulo não é contratado, redirecionar para exames
  useEffect(() => {
    if (activeTab === 'cipa' && !empresaHasCipa) {
      setActiveTab('exames');
    }
    if (activeTab === 'minha_saude' && !empresaHasSaude) {
      setActiveTab('exames');
    }
  }, [activeTab, empresaHasCipa, empresaHasSaude]);

  // Estados da lista de exames
  const [exames, setExames] = useState<ColaboradorExameHistoricoContract[]>([]);
  const [loadingExames, setLoadingExames] = useState(false);
  const [downloadingDocId, setDownloadingDocId] = useState<number | null>(null);
  const [downloadingFichaId, setDownloadingFichaId] = useState<number | null>(null);
  const [confirmedAgendamentoId, setConfirmedAgendamentoId] = useState<number | null>(null);
  const [agendamentoClearedAfterFinish, setAgendamentoClearedAfterFinish] = useState(false);
  const [selectedComprovante, setSelectedComprovante] = useState<ColaboradorExameHistoricoContract | null>(null);

  // Estados do fluxo de agendamento por token
  const [activeToken, setActiveToken] = useState<string | null>(tokenFromUrl);
  const [loadingTokenData, setLoadingTokenData] = useState(false);

  // Sincronizar activeToken caso a URL mude
  useEffect(() => {
    setActiveToken(tokenFromUrl);
  }, [tokenFromUrl]);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [autoagendamentoData, setAutoagendamentoData] = useState<AutoagendamentoData | null>(null);

  // Filtros de seleção de clínica no agendamento
  const [estados, setEstados] = useState<EstadoClinica[]>([]);
  const [selectedEstado, setSelectedEstado] = useState('');
  const [cidades, setCidades] = useState<string[]>([]);
  const [selectedCidade, setSelectedCidade] = useState('');
  const [bairros, setBairros] = useState<string[]>([]);
  const [selectedBairro, setSelectedBairro] = useState('');
  const [clinicas, setClinicas] = useState<ClinicaAutoagendamento[]>([]);
  const [loadingClinicas, setLoadingClinicas] = useState(false);
  const [selectedClinica, setSelectedClinica] = useState<ClinicaAutoagendamento | null>(null);
  const [datasDisponiveis, setDatasDisponiveis] = useState<string[]>([]);
  const [horariosDisponiveis, setHorariosDisponiveis] = useState<string[]>([]);
  const [possuiHoraMarcada, setPossuiHoraMarcada] = useState(false);
  const [horarioFuncionamentoTexto, setHorarioFuncionamentoTexto] = useState<string | null>(null);
  const [loadingDatas, setLoadingDatas] = useState(false);
  const [selectedData, setSelectedData] = useState('');
  const [selectedHorario, setSelectedHorario] = useState('');
  const [submittingAgendamento, setSubmittingAgendamento] = useState(false);
  const [agendamentoConfirmed, setAgendamentoConfirmed] = useState(false);

  // Auto-scroll para o topo quando agendamento for concluído
  useEffect(() => {
    if (agendamentoConfirmed) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [agendamentoConfirmed]);

  // Estados do Canal de Denúncias
  const [denunciaSubTab, setDenunciaSubTab] = useState<'nova' | 'consultar'>('nova');
  const [denunciaForm, setDenunciaForm] = useState<CriarDenunciaAnonimaPayloadContract>({
    tipo: 'assedio_moral',
    setor_local: '',
    data_hora_fato: '',
    envolvidos: '',
    relato: '',
  });
  const [submittingDenuncia, setSubmittingDenuncia] = useState(false);
  const [denunciaProtocoloGerado, setDenunciaProtocoloGerado] = useState<string | null>(null);
  const [copiedProtocolo, setCopiedProtocolo] = useState(false);
  const [consultaProtocoloInput, setConsultaProtocoloInput] = useState('');
  const [consultingDenuncia, setConsultingDenuncia] = useState(false);
  const [denunciaConsultada, setDenunciaConsultada] = useState<ConsultaDenunciaContract | null>(null);
  const [consultaError, setConsultaError] = useState<string | null>(null);

  // Definir título da página e carregar dados salvos do colaborador se houver sessão
  useEffect(() => {
    document.title = 'Portal do Colaborador - SST Exames';

    const saved = localStorage.getItem('portal_colaborador_perfil');
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as ColaboradorPortalPerfilContract;
        setPerfil(parsed);
        if (parsed.is_candidato) {
          setActiveTab((prev) => (prev === 'denuncia' ? 'exames' : prev));
        }
      } catch {
        localStorage.removeItem('portal_colaborador_perfil');
      }
    }
  }, []);

  // Se houver colaborador identificado, carrega seus exames
  useEffect(() => {
    if (perfil?.id) {
      loadExames(perfil.id);
    }
  }, [perfil?.id]);

  // Se houver activeToken, carrega os dados do exame para o seletor de clínicas
  useEffect(() => {
    if (!activeToken) return;

    const loadTokenData = async () => {
      try {
        setLoadingTokenData(true);
        setTokenError(null);

        const res = await agendamentosService.getAutoagendamentoByToken(activeToken);
        setAutoagendamentoData(res);

        if (res.agendamento_id) {
          setConfirmedAgendamentoId(res.agendamento_id);
        }

        if (res.colaborador?.id) {
          const colabPerfil: ColaboradorPortalPerfilContract = {
            id: res.colaborador.id,
            nome: res.colaborador.nome,
            cpf: res.colaborador.cpf,
            email: res.colaborador.email,
            telefone: res.colaborador.telefone,
            cargo: res.colaborador.cargo,
            setor: res.colaborador.setor,
            is_candidato: false,
            empresa: {
              id: res.empresa.id,
              razao_social: res.empresa.razao_social || res.empresa.nome || '',
              nome_fantasia: res.empresa.nome_fantasia || res.empresa.nome,
              cnpj: res.empresa.cnpj || '',
            },
          };
          setPerfil(colabPerfil);
          localStorage.setItem('portal_colaborador_perfil', JSON.stringify(colabPerfil));
          loadExames(res.colaborador.id);
        }

        if (res.ja_confirmado) {
          setAgendamentoConfirmed(true);
          if (!isEmbedded) {
            setAgendamentoClearedAfterFinish(true);
          }
          setLoadingTokenData(false);
          return;
        }

        const estadosRes = await agendamentosService.getEstadosAutoagendamento(activeToken);
        setEstados(estadosRes);
        if (estadosRes.length === 1) {
          const sigla = estadosRes[0].sigla || estadosRes[0].uf || '';
          setSelectedEstado(sigla);
        }
      } catch (err: unknown) {
        const errorObj = err as { response?: { data?: { message?: string } } };
        setTokenError(
          errorObj?.response?.data?.message ||
            'Não foi possível localizar este agendamento. O link pode ser inválido ou já ter expirado.',
        );
      } finally {
        setLoadingTokenData(false);
      }
    };

    loadTokenData();
  }, [activeToken, isEmbedded]);

  // Efeito para carregar cidades quando estado mudar no agendador
  useEffect(() => {
    let isCurrent = true;
    setCidades([]);
    setSelectedCidade('');
    setBairros([]);
    setSelectedBairro('');
    setClinicas([]);
    setSelectedClinica(null);

    if (!activeToken || !selectedEstado) {
      return;
    }

    const loadCidades = async () => {
      try {
        const data = await agendamentosService.getCidadesAutoagendamento(activeToken, selectedEstado);
        if (!isCurrent) return;
        const safeCidades: string[] = Array.isArray(data)
          ? (data as Array<string | Record<string, unknown>>)
              .map((c) => {
                if (typeof c === 'string') return c.trim();
                const obj = typeof c === 'object' && c !== null ? c : null;
                return String(obj?.cidade || obj?.nome || '').trim();
              })
              .filter((c): c is string => Boolean(c && c.length > 0))
          : [];
        setCidades(safeCidades);
        if (safeCidades.length === 1) setSelectedCidade(safeCidades[0]);
      } catch (err) {
        if (!isCurrent) return;
        console.error('Erro ao buscar cidades:', err);
      }
    };
    loadCidades();

    return () => {
      isCurrent = false;
    };
  }, [activeToken, selectedEstado]);

  // Efeito para carregar bairros e clínicas quando cidade mudar
  useEffect(() => {
    if (!activeToken || !selectedEstado || !selectedCidade) {
      setBairros([]);
      setSelectedBairro('');
      setClinicas([]);
      return;
    }
    setSelectedBairro('');
    const loadClinicas = async () => {
      try {
        setLoadingClinicas(true);
        const [bairrosData, clinicasData] = await Promise.all([
          agendamentosService.getBairrosAutoagendamento(activeToken, selectedEstado, selectedCidade),
          agendamentosService.getClinicasAutoagendamento(activeToken, {
            estado: selectedEstado,
            cidade: selectedCidade,
          }),
        ]);
        const safeBairros: string[] = Array.isArray(bairrosData)
          ? (bairrosData as Array<string | Record<string, unknown>>)
              .map((b) => {
                if (typeof b === 'string') return b;
                const obj = typeof b === 'object' && b !== null ? b : null;
                return String(obj?.nome || obj?.bairro || '');
              })
              .filter((b): b is string => Boolean(b && b.trim().length > 0))
          : [];
        const safeClinicas: ClinicaAutoagendamento[] = Array.isArray(clinicasData)
          ? (clinicasData as ClinicaAutoagendamento[]).filter(
              (c): c is ClinicaAutoagendamento => Boolean(c && typeof c === 'object' && c.id),
            )
          : [];
        setBairros(safeBairros);
        setClinicas(safeClinicas);
        if (safeClinicas.length === 1) {
          setSelectedClinica(safeClinicas[0]);
        }
      } catch (err) {
        console.error('Erro ao buscar clínicas:', err);
      } finally {
        setLoadingClinicas(false);
      }
    };
    loadClinicas();
  }, [activeToken, selectedEstado, selectedCidade]);

  // Efeito para carregar agenda quando clínica for selecionada
  useEffect(() => {
    if (!activeToken || !selectedClinica) {
      setDatasDisponiveis([]);
      setHorariosDisponiveis([]);
      setSelectedData('');
      setSelectedHorario('');
      setPossuiHoraMarcada(false);
      setHorarioFuncionamentoTexto(null);
      return;
    }
    const loadAgenda = async () => {
      try {
        setLoadingDatas(true);
        const data = await agendamentosService.getDatasAutoagendamento(activeToken, selectedClinica.id);
        const list: string[] = Array.isArray(data)
          ? (data as Array<{ data?: string } | string>).map((d) => (typeof d === 'string' ? d : d.data || ''))
          : (data?.datas_disponiveis || []);

        const temHoraMarcada = Boolean(!Array.isArray(data) && data?.possui_hora_marcada);
        const hours: string[] = temHoraMarcada && !Array.isArray(data) ? (data?.horarios_padrao || []) : [];
        const funcTexto = !Array.isArray(data) ? data?.horario_funcionamento_texto || null : null;

        setPossuiHoraMarcada(temHoraMarcada);
        setHorarioFuncionamentoTexto(funcTexto);
        setDatasDisponiveis(list);
        setHorariosDisponiveis(hours);
        if (list.length > 0) {
          setSelectedData(list[0]);
        }
        if (temHoraMarcada && hours.length > 0) {
          setSelectedHorario(hours[0]);
        } else {
          setSelectedHorario('');
        }
      } catch (err) {
        console.error('Erro ao buscar agenda da clínica:', err);
      } finally {
        setLoadingDatas(false);
      }
    };
    loadAgenda();
  }, [activeToken, selectedClinica]);

  // Contagem de clínicas por bairro (calculada sobre a lista completa da cidade para não zerar na troca)
  const countsPorBairro = useMemo(() => {
    const map = new Map<string, number>();
    if (!Array.isArray(clinicas)) return map;
    clinicas.forEach((c) => {
      const b = String(c?.bairro || '').trim().toLowerCase();
      if (b) {
        map.set(b, (map.get(b) || 0) + 1);
      }
    });
    return map;
  }, [clinicas]);

  // Clínicas filtradas pelo bairro selecionado (filtro em memória rápido sem disparar requests ou zerar abas)
  const clinicasFiltradas = useMemo(() => {
    if (!Array.isArray(clinicas)) return [];
    if (!selectedBairro) return clinicas;
    const normBairro = String(selectedBairro || '').trim().toLowerCase();
    return clinicas.filter((c) => String(c?.bairro || '').trim().toLowerCase() === normBairro);
  }, [clinicas, selectedBairro]);

  // Função para carregar exames do colaborador
  const loadExames = async (colaboradorId: number) => {
    try {
      setLoadingExames(true);
      const res = await portalColaboradorService.getMeusExames(colaboradorId);
      setExames(res);
    } catch (err) {
      console.error('Erro ao carregar exames:', err);
    } finally {
      setLoadingExames(false);
    }
  };

  const formatDateTimeBR = (dateStr?: string | null) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const handleIrParaConfirmarPresenca = (token?: string | null) => {
    if (token) {
      window.location.href = `/confirmar-presenca/${token}`;
    } else {
      window.location.href = '/confirmar-presenca';
    }
  };

  // Identificação do Colaborador
  const handleIdentificar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cpfInput.trim()) {
      setIdentifyError('Por favor, informe seu CPF.');
      return;
    }
    if (!dataNascimentoInput.trim()) {
      setIdentifyError('Por favor, informe sua data de nascimento.');
      return;
    }

    try {
      setIdentifying(true);
      setIdentifyError(null);
      const resultado = await portalColaboradorService.identificar({
        cpf: cpfInput,
        data_nascimento: dataNascimentoInput,
      });

      setPerfil(resultado);
      localStorage.setItem('portal_colaborador_perfil', JSON.stringify(resultado));
      setActiveTab('exames');
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: { message?: string } } };
      setIdentifyError(
        errorObj?.response?.data?.message ||
          'Não foi possível validar seus dados. Verifique o CPF digitado ou procure o RH da sua empresa.',
      );
    } finally {
      setIdentifying(false);
    }
  };

  // Logout / Trocar Colaborador
  const handleLogout = () => {
    localStorage.removeItem('portal_colaborador_perfil');
    localStorage.removeItem('portal_colaborador_token');
    sessionStorage.removeItem('portal_colaborador_perfil');
    sessionStorage.removeItem('portal_colaborador_token');

    setPerfil(null);
    setActiveToken(null);
    setAutoagendamentoData(null);
    setExames([]);
    setCpfInput('');
    setDataNascimentoInput('');
    setAgendamentoConfirmed(false);
    setAgendamentoClearedAfterFinish(false);
    setSelectedClinica(null);
    setSelectedData('');
    setSelectedHorario('');
    setConfirmedAgendamentoId(null);
    setTokenError(null);
    setActiveTab('exames');
    setIdentifyError(null);

    const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
    const isColabSubdomain =
      hostname.startsWith('colab.') ||
      hostname.includes('colab.sstlocal') ||
      hostname.includes('colab.staging');

    const targetRoute = isColabSubdomain ? '/' : '/portal-colaborador';
    navigate(targetRoute, { replace: true });
  };

  // Transição de abas controlando o término do agendamento temporário
  const handleTabChange = (newTab: 'agendamento' | 'exames' | 'minha_saude' | 'denuncia' | 'cipa') => {
    if (isEmbedded) return;
    if (activeTab === 'agendamento' && agendamentoConfirmed && newTab === 'exames') {
      setAgendamentoClearedAfterFinish(true);
    }
    if (newTab === 'exames') {
      const targetColabId = autoagendamentoData?.colaborador?.id || perfil?.id;
      if (targetColabId) {
        loadExames(targetColabId);
      }
    }
    setActiveTab(newTab);
  };

  // Confirmação de autoagendamento
  const handleConfirmarAgendamento = async () => {
    if (!activeToken) {
      alert('Sessão expirada ou link inválido. Por favor, recarregue a página.');
      return;
    }
    if (!selectedClinica) {
      alert('Por favor, selecione uma clínica para continuar.');
      setMobileWizardStep(2);
      return;
    }
    if (!selectedData) {
      alert('Por favor, selecione o dia de atendimento.');
      return;
    }

    try {
      setSubmittingAgendamento(true);
      const res = await agendamentosService.confirmarAutoagendamento(activeToken, {
        clinica_id: selectedClinica.id,
        data_agendamento: selectedData,
        horario: selectedHorario || undefined,
      });

      if (res?.agendamento_id) {
        setConfirmedAgendamentoId(res.agendamento_id);
      } else if (autoagendamentoData?.agendamento_id) {
        setConfirmedAgendamentoId(autoagendamentoData.agendamento_id);
      }

      setAgendamentoConfirmed(true);
      setAgendamentoClearedAfterFinish(false);
      const targetColabId = perfil?.id || autoagendamentoData?.colaborador?.id;
      if (targetColabId) {
        loadExames(targetColabId);
      }
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: { message?: string } } };
      alert(errorObj?.response?.data?.message || 'Erro ao confirmar agendamento. Tente novamente.');
    } finally {
      setSubmittingAgendamento(false);
    }
  };

  // Submissão de denúncia anônima
  const handleEnviarDenuncia = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!denunciaForm.relato.trim()) {
      alert('Por favor, descreva o relato do ocorrido.');
      return;
    }

    try {
      setSubmittingDenuncia(true);
      const res = await portalColaboradorService.criarDenunciaAnonima({
        ...denunciaForm,
        empresa_id: perfil?.empresa.id || null,
      });

      setDenunciaProtocoloGerado(res.protocolo);
      setDenunciaForm({
        tipo: 'assedio_moral',
        setor_local: '',
        data_hora_fato: '',
        envolvidos: '',
        relato: '',
      });
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: { message?: string } } };
      alert(errorObj?.response?.data?.message || 'Erro ao enviar relato anônimo. Tente novamente.');
    } finally {
      setSubmittingDenuncia(false);
    }
  };

  // Consulta de protocolo de denúncia
  const handleConsultarProtocolo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consultaProtocoloInput.trim()) return;

    try {
      setConsultingDenuncia(true);
      setConsultaError(null);
      setDenunciaConsultada(null);
      const res = await portalColaboradorService.consultarDenuncia(consultaProtocoloInput.trim());
      setDenunciaConsultada(res);
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: { message?: string } } };
      setConsultaError(
        errorObj?.response?.data?.message || 'Nenhum registro encontrado com o protocolo informado.',
      );
    } finally {
      setConsultingDenuncia(false);
    }
  };

  const handleDownloadFicha = async (agendamentoId: number) => {
    try {
      setDownloadingFichaId(agendamentoId);
      await portalColaboradorService.downloadFicha(agendamentoId);
    } catch (err) {
      console.error('Erro ao baixar ficha médica:', err);
      alert('Não foi possível gerar a Ficha Clínica no momento.');
    } finally {
      setDownloadingFichaId(null);
    }
  };

  // Downloads de documentos
  const handleDownloadGuia = async (agendamentoId: number) => {
    try {
      setDownloadingDocId(agendamentoId);
      await portalColaboradorService.downloadGuia(agendamentoId);
    } catch (err) {
      console.error('Erro ao baixar guia:', err);
      alert('Não foi possível gerar a Guia de Encaminhamento no momento.');
    } finally {
      setDownloadingDocId(null);
    }
  };

  const handleDownloadASO = async (agendamentoId: number) => {
    try {
      setDownloadingDocId(agendamentoId);
      await portalColaboradorService.downloadASO(agendamentoId);
    } catch (err) {
      console.error('Erro ao baixar ASO:', err);
      alert('Não foi possível baixar o ASO. O documento pode ainda estar aguardando laudo ou assinatura médica.');
    } finally {
      setDownloadingDocId(null);
    }
  };

  const formatDateBR = (dateStr: string) => {
    if (!dateStr) return '';
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  const formatWeekdayAndDate = (dateStr: string) => {
    if (!dateStr) return { weekday: '', dateFormatted: '' };
    const dateFormatted = formatDateBR(dateStr);
    const dateObj = new Date(`${dateStr.split('T')[0]}T12:00:00Z`);
    const weekday = !isNaN(dateObj.getTime())
      ? new Intl.DateTimeFormat('pt-BR', { weekday: 'short', timeZone: 'UTC' })
          .format(dateObj)
          .replace('.', '')
          .toUpperCase()
      : '';
    return { weekday, dateFormatted };
  };

  const isPendingAgendamento = Boolean(activeToken && !agendamentoConfirmed);

  return (
    <div className="min-h-screen bg-neutral-50 text-gray-800 flex flex-col">
      {/* Barra de Topo do Portal - Oculta no mobile enquanto o agendamento não for realizado */}
      <header
        className={`bg-white border-b border-gray-200 sticky top-0 z-30 shadow-2xs ${
          isPendingAgendamento ? 'hidden sm:block' : ''
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/sst_logo.svg" alt="SST Exames" className="h-8 w-auto object-contain" />
            <div className="h-5 w-px bg-gray-200 hidden sm:block" />
            <span className="text-xs sm:text-sm font-semibold text-gray-700">
              Portal do Colaborador
            </span>
          </div>

          {perfil || autoagendamentoData ? (
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <div className="text-xs font-bold text-gray-900 flex items-center justify-end gap-1.5">
                  {perfil?.nome || autoagendamentoData?.colaborador?.nome}
                  {perfil?.is_candidato && (
                    <span className="text-[10px] bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full font-semibold">
                      Processo Seletivo
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-gray-500">
                  {perfil?.empresa?.razao_social || autoagendamentoData?.empresa?.razao_social || autoagendamentoData?.empresa?.nome}
                </div>
              </div>
              {!isEmbedded && (
                <button
                  type="button"
                  onClick={handleLogout}
                  className="p-2 text-gray-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors flex items-center gap-1.5 text-xs font-semibold"
                  title="Sair do Portal"
                  aria-label="Sair do Portal"
                >
                  <LogOut className="w-4 h-4 text-rose-500 sm:text-gray-400" />
                  <span className="sm:hidden text-rose-600 text-xs font-medium">Sair</span>
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs text-emerald-700 font-semibold bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200 flex items-center gap-1.5 shadow-2xs">
                <ShieldCheck className="w-4 h-4 text-emerald-600" /> Acesso Seguro
              </span>
            </div>
          )}
        </div>
      </header>

      {/* Conteúdo Principal */}
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* SE NÃO ESTIVER IDENTIFICADO E NÃO HOUVER TOKEN: Card de Login Rápido */}
        {!perfil && !activeToken && (
          <div className="max-w-md mx-auto my-8 space-y-6">
            <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm text-center space-y-6">
              <div className="flex justify-center mb-6">
                <img src="/sst_logo.svg" alt="SST Exames" className="h-10 w-auto object-contain" />
              </div>

              <div>
                <h1 className="text-xl font-bold text-gray-900">Acesse Seus Exames Ocupacionais</h1>
                <p className="text-xs text-gray-500 mt-1">
                  Informe seu CPF para consultar agendamentos, emitir comprovantes e baixar seu ASO.
                </p>
              </div>

              {identifyError && (
                <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700 text-left flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
                  <span>{identifyError}</span>
                </div>
              )}

              <form onSubmit={handleIdentificar} className="space-y-4 text-left">
                <FormField label="CPF" required={true}>
                  <FormattedInput
                    type="cpf"
                    required
                    placeholder="000.000.000-00"
                    value={cpfInput}
                    onChange={(val) => setCpfInput(val)}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:outline-hidden focus:ring-2 focus:ring-emerald-500 focus:bg-white"
                  />
                </FormField>

                <FormField label="Data de Nascimento" required={true}>
                  <input
                    type="date"
                    required
                    value={dataNascimentoInput}
                    onChange={(e) => setDataNascimentoInput(e.target.value)}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:outline-hidden focus:ring-2 focus:ring-emerald-500 focus:bg-white"
                  />
                </FormField>

                <button
                  type="submit"
                  disabled={identifying}
                  className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {identifying ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Localizando Cadastro...
                    </>
                  ) : (
                    <>
                      Acessar Meu Painel <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>

              <div className="pt-4 border-t border-gray-100 flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={() => handleTabChange('denuncia')}
                  className="text-emerald-700 hover:underline font-semibold flex items-center gap-1"
                >
                  <ShieldAlert className="w-3.5 h-3.5" /> Canal de Denúncias Anônimo
                </button>
                <a
                  href="/login"
                  className="text-gray-500 hover:text-gray-800 hover:underline flex items-center gap-1 font-medium"
                >
                  Área da Empresa →
                </a>
              </div>
            </div>
          </div>
        )}

        {/* NAVEGAÇÃO POR ABAS DO HUB DO COLABORADOR OU CANDIDATO */}
        {!isEmbedded && (perfil || activeToken) && (
          <>
            {/* Abas no Mobile: Segmented Control moderno (NUNCA exibido durante agendamento pendente) */}
            {!isPendingAgendamento && (
              <div className="sm:hidden bg-gray-200/80 p-1 rounded-2xl flex gap-1 shadow-inner border border-gray-300/50">
                {activeToken && (
                  <button
                    type="button"
                    onClick={() => handleTabChange('agendamento')}
                    className={`flex-1 py-2 px-2 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 ${
                      activeTab === 'agendamento'
                        ? 'bg-white text-emerald-800 shadow-xs ring-1 ring-black/5'
                        : 'text-gray-600 active:bg-gray-300/50'
                    }`}
                  >
                    <Calendar className="w-3.5 h-3.5" /> Agendamento
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => handleTabChange('exames')}
                  className={`flex-1 py-2 px-2 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 ${
                    activeTab === 'exames'
                      ? 'bg-white text-emerald-800 shadow-xs ring-1 ring-black/5'
                      : 'text-gray-600 active:bg-gray-300/50'
                  }`}
                >
                  <Stethoscope className="w-3.5 h-3.5" /> Meus Exames
                </button>

                {!perfil?.is_candidato && empresaHasSaude && (
                  <button
                    type="button"
                    onClick={() => handleTabChange('minha_saude')}
                    className={`flex-1 py-2 px-2 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 ${
                      activeTab === 'minha_saude'
                        ? 'bg-white text-emerald-800 shadow-xs ring-1 ring-black/5'
                        : 'text-gray-600 active:bg-gray-300/50'
                    }`}
                  >
                    <Heart className="w-3.5 h-3.5 text-rose-500" /> Minha Saúde
                  </button>
                )}

                {!perfil?.is_candidato && (
                  <button
                    type="button"
                    onClick={() => handleTabChange('denuncia')}
                    className={`flex-1 py-2 px-2 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 ${
                      activeTab === 'denuncia'
                        ? 'bg-white text-emerald-800 shadow-xs ring-1 ring-black/5'
                        : 'text-gray-600 active:bg-gray-300/50'
                    }`}
                  >
                    <ShieldAlert className="w-3.5 h-3.5" /> Denúncias
                  </button>
                )}

                {!perfil?.is_candidato && empresaHasCipa && (
                  <button
                    type="button"
                    onClick={() => handleTabChange('cipa')}
                    className={`flex-1 py-2 px-2 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 ${
                      activeTab === 'cipa'
                        ? 'bg-white text-emerald-800 shadow-xs ring-1 ring-black/5'
                        : 'text-gray-600 active:bg-gray-300/50'
                    }`}
                  >
                    <ShieldCheck className="w-3.5 h-3.5" /> CIPA NR-5
                  </button>
                )}
              </div>
            )}

            {/* Abas no Desktop: mantido 100% inalterado */}
            <div className="hidden sm:flex flex-wrap gap-2 border-b border-gray-200 pb-2">
              {activeToken && (
                <button
                  type="button"
                  onClick={() => handleTabChange('agendamento')}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                    activeTab === 'agendamento'
                      ? 'bg-emerald-600 text-white shadow-xs'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Calendar className="w-3.5 h-3.5" /> Escolha de Clínica (Exame Atual)
                </button>
              )}

              <button
                type="button"
                onClick={() => handleTabChange('exames')}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                  activeTab === 'exames'
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Stethoscope className="w-3.5 h-3.5" /> Meus Exames e Agendamentos
              </button>

              {!perfil?.is_candidato && empresaHasSaude && (
                <button
                  type="button"
                  onClick={() => handleTabChange('minha_saude')}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                    activeTab === 'minha_saude'
                      ? 'bg-emerald-600 text-white shadow-xs'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Heart className="w-3.5 h-3.5" /> Minha Saúde & Atestados
                </button>
              )}

              {/* Canal de Denúncias: reservado para colaboradores da empresa, nunca exibido para candidatos */}
              {!perfil?.is_candidato && (
                <button
                  type="button"
                  onClick={() => handleTabChange('denuncia')}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                    activeTab === 'denuncia'
                      ? 'bg-emerald-600 text-white shadow-xs'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <ShieldAlert className="w-3.5 h-3.5" /> Canal de Denúncias
                </button>
              )}

              {/* Módulo CIPA & Eleições Secretas: reservado para colaboradores da empresa com CIPA contratada */}
              {!perfil?.is_candidato && empresaHasCipa && (
                <button
                  type="button"
                  onClick={() => handleTabChange('cipa')}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                    activeTab === 'cipa'
                      ? 'bg-emerald-600 text-white shadow-xs'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <ShieldCheck className="w-3.5 h-3.5" /> CIPA & Eleições
                </button>
              )}
            </div>
          </>
        )}

        {/* ABA 1: FLUXO DE AUTOAGENDAMENTO PRIORITÁRIO POR TOKEN */}
        {activeTab === 'agendamento' && activeToken && (
          <div className="space-y-6">
            {loadingTokenData ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                <p className="text-sm text-gray-600 font-medium">Carregando dados do agendamento...</p>
              </div>
            ) : tokenError ? (
              <div className="bg-white rounded-2xl border border-rose-200 p-8 text-center space-y-4">
                <AlertCircle className="w-10 h-10 text-rose-500 mx-auto" />
                <h3 className="text-base font-bold text-gray-900">Agendamento Indisponível</h3>
                <p className="text-xs text-gray-600 max-w-md mx-auto">{tokenError}</p>
              </div>
            ) : agendamentoClearedAfterFinish ? (
              <div className="bg-white rounded-3xl border border-gray-200 p-8 sm:p-12 shadow-sm text-center max-w-xl mx-auto space-y-4">
                <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto border border-emerald-100">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  Você não possui nenhum agendamento pendente no momento
                </h2>
                <p className="text-xs text-gray-500 max-w-md mx-auto leading-relaxed">
                  O seu agendamento já foi finalizado. Você pode acompanhar todas as informações, emitir documentos e acompanhar laudos na aba Meus Exames e Agendamentos.
                </p>
                {!isEmbedded ? (
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => handleTabChange('exames')}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-sm transition-all"
                    >
                      Ver Meus Exames e Agendamentos <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800">
                    Seu agendamento já está confirmado. Apresente-se na clínica com seu documento oficial com foto.
                  </div>
                )}
              </div>
            ) : agendamentoConfirmed ? (
              <div className="bg-white rounded-3xl border border-gray-200 p-8 sm:p-10 shadow-sm space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                  <div className="space-y-5">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-bold border border-emerald-200">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      Agendamento Concluído com Sucesso!
                    </div>

                    <h1 className="text-2xl sm:text-3xl font-black text-gray-900 leading-tight">
                      Pronto! Seu exame já está agendado na clínica.
                    </h1>

                    <p className="text-sm text-gray-600 leading-relaxed">
                      Baixe abaixo sua <strong>Guia de Encaminhamento</strong> e sua <strong>Ficha Clínica</strong> para levar no dia do atendimento. Compareça munido de documento oficial com foto.
                    </p>

                    {/* Resumo da Clínica e Data */}
                    <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 space-y-2.5 text-xs">
                      {selectedClinica && (
                        <div className="flex items-start gap-2">
                          <Building2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                          <div>
                            <span className="font-bold text-gray-900 block">{selectedClinica.nome}</span>
                            <span className="text-gray-500">{selectedClinica.endereco_completo}</span>
                          </div>
                        </div>
                      )}

                      {selectedData && (
                        <div className="flex items-center gap-2 pt-2 border-t border-gray-200/60">
                          <Calendar className="w-4 h-4 text-emerald-600 shrink-0" />
                          <span className="text-gray-700">
                            Data: <strong>{formatDateBR(selectedData)}</strong>{' '}
                            {selectedHorario ? (
                              <>às <strong>{selectedHorario}</strong></>
                            ) : (
                              <span className="text-gray-500 font-normal">• Atendimento por ordem de chegada</span>
                            )}
                          </span>
                        </div>
                      )}

                      {(horarioFuncionamentoTexto || selectedClinica?.horario_funcionamento_texto) && (
                        <div className="flex items-center gap-2 pt-2 border-t border-gray-200/60 text-gray-700">
                          <Clock className="w-4 h-4 text-emerald-600 shrink-0" />
                          <span>
                            <strong>Horário de Funcionamento:</strong>{' '}
                            {horarioFuncionamentoTexto || selectedClinica?.horario_funcionamento_texto}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Botões de Ação Imediatos: Guia e Ficha - apenas se confirmados com clínica e data, SEM fallback */}
                    {confirmedAgendamentoId && selectedClinica && selectedData && (
                      <div className="flex flex-col sm:flex-row gap-3 pt-2">
                        <button
                          type="button"
                          onClick={() => handleDownloadGuia(confirmedAgendamentoId)}
                          disabled={downloadingDocId !== null}
                          className="flex-1 py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          {downloadingDocId ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : isMobileDevice ? (
                            <ExternalLink className="w-4 h-4" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                          {isMobileDevice ? 'Visualizar Guia de Encaminhamento' : 'Baixar Guia de Encaminhamento'}
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDownloadFicha(confirmedAgendamentoId)}
                          disabled={downloadingFichaId !== null}
                          className="flex-1 py-3 px-4 bg-white hover:bg-gray-50 text-gray-800 font-bold text-xs rounded-xl border border-gray-300 shadow-xs transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          {downloadingFichaId ? (
                            <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                          ) : isMobileDevice ? (
                            <ExternalLink className="w-4 h-4 text-emerald-600" />
                          ) : (
                            <FileText className="w-4 h-4 text-emerald-600" />
                          )}
                          {isMobileDevice ? 'Visualizar Ficha Clínica' : 'Baixar Ficha Clínica'}
                        </button>
                      </div>
                    )}

                    {/* Aviso sobre o ASO condicionado */}
                    {!isEmbedded ? (
                      <div className="p-3.5 bg-blue-50/70 border border-blue-200/80 rounded-xl text-xs text-blue-800 flex items-start gap-2.5">
                        <AlertCircle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                        <div>
                          <strong className="block text-blue-900">Sobre seu ASO (Atestado de Saúde Ocupacional)</strong>
                          <span>O ASO estará disponível para download na aba <em>Meus Exames e Agendamentos</em> assim que você realizar o exame na clínica e o médico emitir o laudo final.</span>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-emerald-50 border border-emerald-200/80 rounded-2xl text-xs text-emerald-900 flex items-start gap-3 shadow-2xs">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                        <div className="space-y-1">
                          <strong className="block text-emerald-950 font-bold text-sm">
                            Agendamento Confirmado!
                          </strong>
                          <p className="text-emerald-800 leading-relaxed">
                            Seu agendamento foi registrado com sucesso. Compareça à clínica no dia e horário escolhidos portando documento oficial com foto.
                          </p>
                          <p className="text-[11px] text-emerald-700 font-medium pt-1">
                            Para baixar comprovantes ou acessar o histórico completo de exames, abra o link no navegador padrão do seu celular (Chrome ou Safari) ou solicite ao RH da sua empresa.
                          </p>
                        </div>
                      </div>
                    )}

                    {!isEmbedded && (
                      <div className="pt-2">
                        <button
                          type="button"
                          onClick={() => handleTabChange('exames')}
                          className="text-xs font-bold text-emerald-700 hover:text-emerald-800 transition-colors flex items-center gap-1"
                        >
                          Ver histórico em Meus Exames e Agendamentos <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-center items-center">
                    <img
                      src="/image/final.svg"
                      alt="Agendamento Confirmado"
                      className="w-full max-w-sm sm:max-w-md h-auto object-contain drop-shadow-xs"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* ======================================================== */}
                {/* FLUXO MOBILE: WIZARD PASSO A PASSO (SEM ROLAGEM)        */}
                {/* ======================================================== */}
                <div className="sm:hidden space-y-3.5 pb-28">
                  {/* Barra Superior do Wizard Mobile com Indicador de Progresso */}
                  <div className="bg-white rounded-2xl border border-gray-200 p-3.5 shadow-xs space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <img src="/sst_logo.svg" alt="SST Exames" className="h-5 w-auto object-contain" />
                        <span className="text-xs font-bold text-gray-800">Agendamento de Exame</span>
                      </div>
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                        Etapa {mobileWizardStep} de 3
                      </span>
                    </div>

                    {/* Barra de Progresso com 3 segmentos */}
                    <div className="grid grid-cols-3 gap-1.5 pt-0.5">
                      <div className={`h-1.5 rounded-full transition-all ${mobileWizardStep >= 1 ? 'bg-emerald-600' : 'bg-gray-200'}`} />
                      <div className={`h-1.5 rounded-full transition-all ${mobileWizardStep >= 2 ? 'bg-emerald-600' : 'bg-gray-200'}`} />
                      <div className={`h-1.5 rounded-full transition-all ${mobileWizardStep >= 3 ? 'bg-emerald-600' : 'bg-gray-200'}`} />
                    </div>

                    <div className="flex justify-between text-[10px] font-semibold text-gray-400 px-0.5">
                      <span className={mobileWizardStep === 1 ? 'text-emerald-700 font-bold' : ''}>1. Região</span>
                      <span className={mobileWizardStep === 2 ? 'text-emerald-700 font-bold' : ''}>2. Clínica</span>
                      <span className={mobileWizardStep === 3 ? 'text-emerald-700 font-bold' : ''}>3. Data e Horário</span>
                    </div>
                  </div>

                  {/* Resumo do Colaborador e Exame (Compacto com Chevron) */}
                  <div className="bg-emerald-50/70 border border-emerald-200/80 rounded-2xl p-3 shadow-2xs transition-all">
                    <div
                      onClick={() => setMobileDetailsOpen(!mobileDetailsOpen)}
                      className="flex items-center justify-between cursor-pointer gap-2 select-none"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-8 h-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold text-xs shrink-0 shadow-2xs">
                          <User className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-bold text-emerald-950 truncate">
                            Olá, {autoagendamentoData?.colaborador.nome}
                          </div>
                          <div className="text-[10px] text-emerald-700 truncate">
                            {autoagendamentoData?.exames?.length || 1} exame(s) • {autoagendamentoData?.empresa.razao_social || autoagendamentoData?.empresa.nome_fantasia || autoagendamentoData?.empresa.nome || 'Empresa'}
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="p-1 text-emerald-700 hover:text-emerald-900 rounded-lg shrink-0 flex items-center gap-1 text-[11px] font-semibold"
                        aria-label="Alternar detalhes do exame"
                      >
                        <span className="text-[10px] text-emerald-800 font-medium">
                          {mobileDetailsOpen ? 'Ocultar' : 'Detalhes'}
                        </span>
                        <ChevronDown
                          className={`w-4 h-4 transition-transform duration-200 ${
                            mobileDetailsOpen ? 'transform rotate-180' : ''
                          }`}
                        />
                      </button>
                    </div>

                    {/* Conteúdo expandido suavemente */}
                    {mobileDetailsOpen && (
                      <div className="mt-3 pt-3 border-t border-emerald-200/70 space-y-2.5 text-xs animate-in fade-in duration-200">
                        <div className="grid grid-cols-2 gap-2 text-[11px]">
                          <div>
                            <span className="text-emerald-700 font-semibold block">Cargo / Função:</span>
                            <strong className="text-emerald-950">{autoagendamentoData?.colaborador.cargo || 'Não informado'}</strong>
                          </div>
                          <div>
                            <span className="text-emerald-700 font-semibold block">Setor:</span>
                            <strong className="text-emerald-950">{autoagendamentoData?.colaborador.setor || 'Não informado'}</strong>
                          </div>
                        </div>

                        {autoagendamentoData?.exames && autoagendamentoData.exames.length > 0 && (
                          <div className="pt-2 border-t border-emerald-200/50">
                            <span className="text-[11px] font-semibold text-emerald-800 block mb-1.5">
                              Exames a Realizar:
                            </span>
                            <div className="flex flex-wrap gap-1.5">
                              {autoagendamentoData.exames.map((ex) => (
                                <span
                                  key={ex.id}
                                  className="inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] font-bold bg-white text-emerald-900 border border-emerald-200 shadow-2xs"
                                >
                                  <Stethoscope className="w-3 h-3 mr-1 text-emerald-600 shrink-0" />
                                  {ex.nome}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ETAPA 1 DO WIZARD: REGIÃO */}
                  {mobileWizardStep === 1 && (
                    <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm space-y-4 notranslate" translate="no">
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-emerald-600" />
                          Onde você prefere realizar o exame?
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Selecione o estado e a cidade para ver as clínicas disponíveis.
                        </p>
                      </div>

                      {/* Seleção do Estado */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          Estado (UF) <span className="text-rose-500">*</span>
                        </label>
                        {estados.length === 1 ? (
                          <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-between text-xs notranslate" translate="no">
                            <div className="flex items-center gap-2">
                              <span className="w-6 h-6 rounded-lg bg-emerald-100 text-emerald-800 font-bold text-xs flex items-center justify-center">
                                {estados[0].sigla || estados[0].uf || selectedEstado}
                              </span>
                              <span className="font-semibold text-gray-900">
                                {estados[0].nome || estados[0].sigla || estados[0].uf || selectedEstado}
                              </span>
                            </div>
                            <span className="text-[10px] text-gray-400 font-medium">(Único disponível)</span>
                          </div>
                        ) : (
                          <StateSelect
                            value={selectedEstado}
                            onChange={(val) => {
                              setSelectedEstado(val);
                              setSelectedCidade('');
                              setSelectedBairro('');
                              setSelectedClinica(null);
                              setSelectedData('');
                            }}
                            estados={estados}
                            required
                            placeholder="Selecione o estado..."
                            className="w-full text-xs"
                          />
                        )}
                      </div>

                      {/* Seleção da Cidade */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          Cidade <span className="text-rose-500">*</span>
                        </label>
                        <SearchableSelect
                          options={(Array.isArray(cidades) ? cidades : [])
                            .map((rawC) => {
                              const c =
                                typeof rawC === 'string'
                                  ? rawC
                                  : String(
                                      (rawC as Record<string, unknown>)?.cidade ||
                                        (rawC as Record<string, unknown>)?.nome ||
                                        '',
                                    );
                              return { id: c, label: c, value: c };
                            })
                            .filter((opt) => Boolean(opt.label))}
                          value={selectedCidade}
                          onChange={(val) => {
                            const newCity = String(val || '');
                            setSelectedCidade(newCity);
                            setSelectedBairro('');
                            setSelectedClinica(null);
                            setSelectedData('');
                            // Avança automaticamente para o passo 2 ao escolher a cidade
                            if (newCity) {
                              setMobileWizardStep(2);
                            }
                          }}
                          disabled={!selectedEstado || cidades.length === 0}
                          required
                          placeholder={!selectedEstado ? 'Selecione o estado primeiro' : 'Toque para escolher a cidade...'}
                          searchPlaceholder="Buscar cidade..."
                          className="w-full text-xs"
                        />
                      </div>

                      {selectedCidade && (
                        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-900 flex items-center justify-between notranslate" translate="no">
                          <span className="flex items-center gap-1.5 font-semibold">
                            <MapPin className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                            Cidade: {selectedCidade} ({selectedEstado})
                          </span>
                          <button
                            type="button"
                            onClick={() => setMobileWizardStep(2)}
                            className="text-xs font-bold text-emerald-700 underline"
                          >
                            Ver Clínicas →
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ETAPA 2 DO WIZARD: ESCOLHA DA CLÍNICA */}
                  {mobileWizardStep === 2 && (
                    <div id="mobile-wizard-step-2" className="scroll-mt-3 bg-white rounded-2xl border border-gray-200 p-4 shadow-sm space-y-4 notranslate" translate="no">
                      {/* Cabeçalho com botão Voltar e Localização */}
                      <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                        <button
                          type="button"
                          onClick={() => setMobileWizardStep(1)}
                          className="text-xs font-bold text-gray-600 hover:text-gray-900 flex items-center gap-1 p-1 -ml-1 rounded-lg"
                        >
                          <ChevronLeft className="w-4 h-4" /> Trocar Cidade
                        </button>
                        <span className="text-[11px] font-semibold text-emerald-800 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200 flex items-center gap-1 notranslate" translate="no">
                          <MapPin className="w-3 h-3 text-emerald-600" />
                          {selectedCidade} - {selectedEstado}
                        </span>
                      </div>

                      <div>
                        <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-emerald-600" />
                          Escolha a Clínica Credenciada
                        </h3>
                        <p className="text-xs text-emerald-900 font-medium mt-1 bg-emerald-50/80 p-2.5 rounded-xl border border-emerald-200/60 flex items-center gap-1.5">
                          <span>👉</span> Toque na clínica abaixo para avançar para a data.
                        </p>
                      </div>

                      {/* Chips de filtro de bairro na própria Etapa 2 */}
                      {bairros.length > 0 && (
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                            <span>Filtrar por Bairro (opcional):</span>
                            {selectedBairro && (
                              <button
                                type="button"
                                onClick={() => setSelectedBairro('')}
                                className="text-emerald-700 font-bold underline lowercase tracking-normal"
                              >
                                ver todos
                              </button>
                            )}
                          </div>
                          <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-none -mx-1 px-1">
                            <button
                              type="button"
                              onClick={() => setSelectedBairro('')}
                              className={`px-3 py-1.5 rounded-xl text-xs font-bold shrink-0 transition-all ${
                                !selectedBairro
                                  ? 'bg-emerald-600 text-white shadow-xs'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              Todos os bairros ({clinicas.length})
                            </button>
                            {(Array.isArray(bairros) ? bairros : []).map((rawB, idx) => {
                              const bObj = typeof rawB === 'object' && rawB !== null ? (rawB as Record<string, unknown>) : null;
                              const b = typeof rawB === 'string' ? rawB : String(bObj?.nome || bObj?.bairro || '');
                              if (!b || !b.trim()) return null;
                              const isSel = String(selectedBairro || '').trim().toLowerCase() === b.trim().toLowerCase();
                              const count = countsPorBairro.get(b.trim().toLowerCase()) || 0;
                              return (
                                <button
                                  key={`${b}-${idx}`}
                                  type="button"
                                  onClick={() => setSelectedBairro(isSel ? '' : b)}
                                  className={`px-3 py-1.5 rounded-xl text-xs font-bold shrink-0 transition-all ${
                                    isSel
                                      ? 'bg-emerald-600 text-white shadow-xs'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                                >
                                  {b} ({count})
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Lista de Clínicas com Scroll Interno Limpo */}
                      {loadingClinicas ? (
                        <div className="py-12 text-center text-xs text-gray-500">
                          <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                          Buscando clínicas credenciadas...
                        </div>
                      ) : clinicasFiltradas.length === 0 ? (
                        <div className="py-8 text-center text-xs text-gray-500 space-y-2">
                          <AlertCircle className="w-8 h-8 text-amber-500 mx-auto" />
                          <p className="font-semibold text-gray-700">Nenhuma clínica encontrada para este filtro.</p>
                          {selectedBairro && (
                            <button
                              type="button"
                              onClick={() => setSelectedBairro('')}
                              className="text-emerald-700 font-bold underline"
                            >
                              Ver todas as clínicas da cidade
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2.5 max-h-[50vh] overflow-y-auto overscroll-contain pr-0.5">
                          {clinicasFiltradas
                            .map((cl) => {
                              const isSelected = selectedClinica?.id === cl.id;
                              return (
                                <div
                                  key={cl.id}
                                  onClick={() => {
                                    setSelectedClinica(cl);
                                    setMobileWizardStep(3);
                                  }}
                                  className={`p-3.5 rounded-2xl border-2 transition-all cursor-pointer flex flex-col gap-2.5 active:scale-[0.99] ${
                                    isSelected
                                      ? 'border-emerald-600 bg-emerald-50/50 shadow-xs ring-2 ring-emerald-500/20'
                                      : 'border-gray-200 active:bg-gray-50 bg-white hover:border-emerald-300'
                                  }`}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                      <div className="flex flex-wrap items-center gap-1.5 mb-1">
                                        <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100/70 px-2 py-0.5 rounded-full">
                                          {cl.bairro || cl.cidade || 'Unidade'}
                                        </span>
                                        {cl.possui_hora_marcada ? (
                                          <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200">
                                            Hora Marcada
                                          </span>
                                        ) : (
                                          <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                                            Ordem de Chegada
                                          </span>
                                        )}
                                      </div>
                                      <h4 className="text-xs font-bold text-gray-900 leading-snug">
                                        {cl.nome}
                                      </h4>
                                    </div>

                                    <div className="shrink-0">
                                      <span
                                        className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                                          isSelected
                                            ? 'bg-emerald-600 text-white'
                                            : 'border-2 border-gray-300 text-transparent'
                                        }`}
                                      >
                                        <CheckCircle2 className="w-4 h-4" />
                                      </span>
                                    </div>
                                  </div>

                                  <p className="text-[11px] text-gray-600 flex items-start gap-1 leading-snug">
                                    <MapPin className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                                    <span>
                                      {cl.endereco_completo ||
                                        [cl.endereco, cl.numero, cl.bairro].filter(Boolean).join(', ')}
                                    </span>
                                  </p>

                                  <div className="text-[10px] text-gray-600 bg-gray-50 px-2.5 py-1.5 rounded-xl flex items-center gap-1.5 border border-gray-100">
                                    <Clock className="w-3 h-3 text-emerald-600 shrink-0" />
                                    <span className="truncate">
                                      {cl.horario_funcionamento_texto || 'Seg a Sex, das 08:00 às 17:00'}
                                    </span>
                                  </div>

                                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                                    <span className="text-[11px] font-semibold text-emerald-700">
                                      Toque para selecionar
                                    </span>
                                    <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-100/80 px-2.5 py-1 rounded-lg">
                                      Escolher clínica <ArrowRight className="w-3 h-3" />
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ETAPA 3 DO WIZARD: DATA E CONFIRMAÇÃO */}
                  {mobileWizardStep === 3 && selectedClinica && (
                    <div id="mobile-wizard-step-3" className="scroll-mt-3 bg-white rounded-2xl border border-gray-200 p-4 shadow-sm space-y-4">
                      {/* Cabeçalho com botão Voltar */}
                      <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                        <button
                          type="button"
                          onClick={() => setMobileWizardStep(2)}
                          className="text-xs font-bold text-gray-600 hover:text-gray-900 flex items-center gap-1 p-1 -ml-1 rounded-lg"
                        >
                          <ChevronLeft className="w-4 h-4" /> Trocar Clínica
                        </button>
                        <span className="text-[11px] font-bold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded-full">
                          Etapa Final
                        </span>
                      </div>

                      {/* Resumo da Clínica Escolhida */}
                      <div className="p-3 bg-emerald-50/70 border border-emerald-200 rounded-xl space-y-1">
                        <div className="text-xs font-bold text-emerald-950 flex items-center gap-1.5">
                          <Building2 className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
                          {selectedClinica.nome}
                        </div>
                        <div className="text-[11px] text-emerald-800 flex items-start gap-1">
                          <MapPin className="w-3 h-3 text-emerald-600 shrink-0 mt-0.5" />
                          <span className="truncate">{selectedClinica.endereco_completo}</span>
                        </div>
                      </div>

                      {loadingDatas ? (
                        <div className="py-8 text-center text-xs text-gray-500">
                          <div className="w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                          Consultando datas disponíveis...
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Seleção de Data */}
                          <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                              Selecione o Dia de Atendimento *
                            </label>
                            <div className="grid grid-cols-3 gap-1.5 max-h-48 overflow-y-auto pr-0.5">
                              {datasDisponiveis.map((dt) => {
                                const { weekday, dateFormatted } = formatWeekdayAndDate(dt);
                                const isSel = selectedData === dt;
                                return (
                                  <button
                                    key={dt}
                                    type="button"
                                    onClick={() => setSelectedData(dt)}
                                    className={`p-2 rounded-xl border text-center transition-all flex flex-col items-center justify-center gap-0.5 ${
                                      isSel
                                        ? 'border-emerald-600 bg-emerald-600 text-white shadow-2xs font-bold'
                                        : 'border-gray-200 bg-white text-gray-800 hover:border-emerald-300'
                                    }`}
                                  >
                                    {weekday && (
                                      <span className="text-[9px] uppercase font-bold opacity-80">{weekday}</span>
                                    )}
                                    <span className="text-xs font-bold">{dateFormatted}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* Seleção de Horário */}
                          {possuiHoraMarcada && horariosDisponiveis.length > 0 ? (
                            <div>
                              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                                Horário do Exame *
                              </label>
                              <div className="grid grid-cols-3 gap-1.5 max-h-36 overflow-y-auto pr-0.5">
                                {horariosDisponiveis.map((hr) => {
                                  const isSel = selectedHorario === hr;
                                  return (
                                    <button
                                      key={hr}
                                      type="button"
                                      onClick={() => setSelectedHorario(hr)}
                                      className={`py-2 px-1 rounded-xl border text-xs font-bold transition-all text-center ${
                                        isSel
                                          ? 'border-emerald-600 bg-emerald-600 text-white shadow-2xs'
                                          : 'border-gray-200 bg-white text-gray-700'
                                      }`}
                                    >
                                      {hr}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ) : (
                            <div className="p-3 bg-blue-50/80 border border-blue-200/80 rounded-xl flex items-start gap-2 text-xs text-blue-950">
                              <Clock className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                              <div>
                                <span className="font-bold block text-blue-900 text-xs">Atendimento por ordem de chegada</span>
                                <span className="text-blue-700 text-[11px] leading-relaxed block mt-0.5">
                                  {horarioFuncionamentoTexto || selectedClinica.horario_funcionamento_texto || 'Horário de funcionamento: Seg a Sex das 08:00 às 17:00'}
                                </span>
                              </div>
                            </div>
                          )}

                          {selectedData && (
                            <div className="text-[11px] text-gray-600 text-center font-medium bg-gray-50 p-2 rounded-xl">
                              Data selecionada: <strong className="text-emerald-900">{formatDateBR(selectedData)}</strong>
                              {selectedHorario ? ` às ${selectedHorario}` : ' (ordem de chegada)'}
                            </div>
                          )}

                          {/* Botão de confirmação no próprio corpo do card (Mobile) */}
                          <div className="pt-2">
                            <button
                              type="button"
                              onClick={handleConfirmarAgendamento}
                              disabled={submittingAgendamento || !selectedData}
                              className="w-full py-3.5 px-4 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold text-xs rounded-xl shadow-md transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                              {submittingAgendamento ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  Confirmando...
                                </>
                              ) : (
                                <>
                                  <CheckCircle2 className="w-4 h-4" />
                                  Confirmar Agendamento
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ======================================================== */}
                  {/* BARRA DE AÇÃO INFERIOR FIXA NO MOBILE (SEMPRE VISÍVEL)   */}
                  {/* ======================================================== */}
                  <div className="fixed bottom-0 left-0 right-0 p-3 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-2xl z-40 sm:hidden">
                    {mobileWizardStep === 1 && (
                      <button
                        type="button"
                        disabled={!selectedCidade}
                        onClick={() => setMobileWizardStep(2)}
                        className="w-full py-3.5 px-4 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-2"
                      >
                        <span>
                          {selectedCidade
                            ? `Avançar: Ver Clínicas em ${selectedCidade} →`
                            : 'Selecione a cidade para avançar'}
                        </span>
                      </button>
                    )}

                    {mobileWizardStep === 2 && (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setMobileWizardStep(1)}
                          className="w-1/3 py-3 px-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-xs rounded-xl transition-all"
                        >
                          ← Voltar
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (selectedClinica) {
                              setMobileWizardStep(3);
                            } else {
                              const target = clinicasFiltradas[0] || clinicas[0];
                              if (target) {
                                setSelectedClinica(target);
                                setMobileWizardStep(3);
                              }
                            }
                          }}
                          className="flex-1 py-3 px-3 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-1.5"
                        >
                          <span>
                            {selectedClinica
                              ? 'Avançar: Escolher Data →'
                              : 'Avançar com 1ª Clínica →'}
                          </span>
                        </button>
                      </div>
                    )}

                    {mobileWizardStep === 3 && (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setMobileWizardStep(2)}
                          className="w-1/3 py-3 px-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-xs rounded-xl transition-all"
                        >
                          ← Voltar
                        </button>
                        <button
                          type="button"
                          onClick={handleConfirmarAgendamento}
                          disabled={submittingAgendamento || !selectedData}
                          className="flex-1 py-3.5 px-4 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold text-xs rounded-xl shadow-md transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          {submittingAgendamento ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Confirmando...
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="w-4 h-4" />
                              Confirmar Agendamento
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* ======================================================== */}
                {/* FLUXO DESKTOP: LAYOUT COMPLETO ORIGINAL (100% INTACTO)   */}
                {/* ======================================================== */}
                <div className="hidden sm:block space-y-6">
                {/* Cabeçalho do Exame */}
                <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse"></span>
                    <h2 className="text-base font-bold text-gray-900">
                      Escolha a Clínica Mais Próxima para Realizar Seu Exame
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-xs bg-gray-50 p-4 rounded-xl">
                    <div>
                      <span className="text-gray-400 block font-semibold">Colaborador:</span>
                      <strong className="text-gray-900">{autoagendamentoData?.colaborador.nome}</strong>
                    </div>
                    <div>
                      <span className="text-gray-400 block font-semibold">Empresa:</span>
                      <strong className="text-gray-900">
                        {autoagendamentoData?.empresa.razao_social ||
                          autoagendamentoData?.empresa.nome_fantasia ||
                          autoagendamentoData?.empresa.nome ||
                          'Não informada'}
                      </strong>
                    </div>
                    <div>
                      <span className="text-gray-400 block font-semibold">Função / Cargo:</span>
                      <strong className="text-gray-900">{autoagendamentoData?.colaborador.cargo || 'Não informado'}</strong>
                    </div>
                    <div>
                      <span className="text-gray-400 block font-semibold">Setor / Departamento:</span>
                      <strong className="text-gray-900">{autoagendamentoData?.colaborador.setor || 'Não informado'}</strong>
                    </div>
                  </div>

                  {autoagendamentoData?.exames && autoagendamentoData.exames.length > 0 && (
                    <div className="pt-2 border-t border-gray-100">
                      <span className="text-xs font-semibold text-gray-500 block mb-2">Exames a Realizar:</span>
                      <div className="flex flex-wrap gap-2">
                        {autoagendamentoData.exames.map((ex) => (
                          <span
                            key={ex.id}
                            className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 shadow-2xs"
                          >
                            <Stethoscope className="w-3.5 h-3.5 mr-1.5 text-emerald-600" />
                            {ex.nome}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Passo 1: Localização */}
                <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-4 notranslate" translate="no">
                  <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs">1</span>
                    Selecione a Região de Atendimento
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    <div className="sm:relative sm:z-30">
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Estado (UF) <span className="text-rose-500">*</span>
                      </label>
                      <StateSelect
                        value={selectedEstado}
                        onChange={(val) => {
                          setSelectedEstado(val);
                          setSelectedCidade('');
                          setSelectedBairro('');
                        }}
                        estados={estados}
                        required
                        placeholder="Selecione o estado..."
                        className="w-full text-xs sm:text-sm"
                      />
                    </div>

                    <div className="sm:relative sm:z-20">
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Cidade <span className="text-rose-500">*</span>
                      </label>
                      <SearchableSelect
                        options={(Array.isArray(cidades) ? cidades : [])
                          .map((rawC) => {
                            const c =
                              typeof rawC === 'string'
                              ? rawC
                              : String(
                                  (rawC as Record<string, unknown>)?.cidade ||
                                    (rawC as Record<string, unknown>)?.nome ||
                                    '',
                                );
                            return { id: c, label: c, value: c };
                          })
                          .filter((opt) => Boolean(opt.label))}
                        value={selectedCidade}
                        onChange={(val) => {
                          setSelectedCidade(String(val || ''));
                          setSelectedBairro('');
                        }}
                        disabled={!selectedEstado || cidades.length === 0}
                        required
                        placeholder={!selectedEstado ? 'Selecione o estado primeiro' : 'Selecione a cidade...'}
                        searchPlaceholder="Buscar cidade..."
                        className="w-full text-xs sm:text-sm"
                      />
                    </div>

                    <div className="sm:relative sm:z-10">
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Bairro (opcional)</label>
                      <SearchableSelect
                        options={[
                          { id: '', label: 'Todos os bairros', value: '' },
                          ...(Array.isArray(bairros) ? bairros : [])
                            .map((rawB) => {
                              const bObj = typeof rawB === 'object' && rawB !== null ? (rawB as Record<string, unknown>) : null;
                              const b = typeof rawB === 'string' ? rawB : String(bObj?.nome || bObj?.bairro || '');
                              return { id: b, label: b, value: b };
                            })
                            .filter((opt) => Boolean(opt.label)),
                        ]}
                        value={selectedBairro}
                        onChange={(val) => setSelectedBairro(String(val || ''))}
                        disabled={!selectedCidade || bairros.length === 0}
                        placeholder={!selectedCidade ? 'Selecione a cidade primeiro' : 'Todos os bairros'}
                        searchPlaceholder="Buscar bairro..."
                        className="w-full text-xs sm:text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Passo 2: Seleção de Clínica */}
                {selectedCidade && (
                  <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-4">
                    <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs">2</span>
                      Escolha a Clínica Credenciada
                    </h3>

                    {loadingClinicas ? (
                      <div className="py-8 text-center text-xs text-gray-500">
                        <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                        Buscando clínicas credenciadas...
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                        {clinicasFiltradas
                          .map((cl) => {
                            const isSelected = selectedClinica?.id === cl.id;
                            return (
                              <div
                                key={cl.id}
                                onClick={() => setSelectedClinica(cl)}
                                className={`p-4 sm:p-5 rounded-2xl border-2 transition-all cursor-pointer flex flex-col justify-between gap-3 ${
                                  isSelected
                                    ? 'border-emerald-600 bg-emerald-50/40 shadow-sm ring-2 ring-emerald-500/20'
                                    : 'border-gray-200 hover:border-emerald-300 bg-white shadow-2xs hover:shadow-sm'
                                }`}
                              >
                                <div className="space-y-2">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex flex-wrap items-center gap-1.5">
                                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-800 bg-emerald-100/70 px-2.5 py-0.5 rounded-full border border-emerald-200/80">
                                        <Building2 className="w-3 h-3 text-emerald-700 shrink-0" />
                                        {cl.bairro ? `${cl.bairro}` : (cl.cidade || 'Unidade')}
                                      </span>
                                      {cl.possui_hora_marcada ? (
                                        <span className="inline-flex items-center text-[10px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200">
                                          Hora Marcada
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                                          Ordem de Chegada
                                        </span>
                                      )}
                                    </div>

                                    <span
                                      className={`px-2.5 py-1 rounded-full text-[11px] font-bold shrink-0 transition-colors ${
                                        isSelected
                                          ? 'bg-emerald-600 text-white shadow-2xs flex items-center gap-1'
                                          : 'bg-gray-100 text-gray-600'
                                      }`}
                                    >
                                      {isSelected && <CheckCircle2 className="w-3.5 h-3.5" />}
                                      {isSelected ? 'Selecionada' : 'Selecionar'}
                                    </span>
                                  </div>

                                  <div>
                                    <h4 className="text-xs sm:text-sm font-bold text-gray-900 leading-snug">
                                      {cl.nome}
                                    </h4>
                                    <p className="text-xs text-gray-600 flex items-start gap-1.5 leading-relaxed mt-1">
                                      <MapPin className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                                      <span>
                                        {cl.endereco_completo ||
                                          [cl.endereco, cl.numero, cl.bairro, cl.cidade].filter(Boolean).join(', ')}
                                      </span>
                                    </p>
                                  </div>
                                </div>

                                {/* Horário de Funcionamento em destaque */}
                                <div className="pt-2.5 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-700 bg-gray-50/70 -mx-1 px-3 py-2 rounded-xl">
                                  <Clock className="w-4 h-4 text-emerald-600 shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <span className="font-semibold text-gray-900 block sm:inline sm:mr-1">
                                      Horário de Atendimento:
                                    </span>
                                    <span className="text-gray-600">
                                      {cl.horario_funcionamento_texto || 'Segunda a Sexta, das 08:00 às 17:00'}
                                    </span>
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedClinica(cl);
                                  }}
                                  className={`w-full py-2.5 px-4 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                                    isSelected
                                      ? 'bg-emerald-600 text-white shadow-xs'
                                      : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200'
                                  }`}
                                >
                                  {isSelected ? (
                                    <>
                                      <CheckCircle2 className="w-4 h-4" />
                                      Clínica Escolhida
                                    </>
                                  ) : (
                                    'Escolher esta Clínica'
                                  )}
                                </button>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                )}

                {/* Passo 3: Data e Confirmação */}
                {selectedClinica && (
                  <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6 shadow-sm space-y-4 sm:space-y-5">
                    <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs">3</span>
                      Data do Atendimento
                    </h3>

                    {/* Resumo da Clínica Selecionada */}
                    <div className="p-4 bg-emerald-50/70 border border-emerald-200 rounded-2xl space-y-2">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="text-xs sm:text-sm font-bold text-emerald-950 flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-emerald-700 shrink-0" />
                          {selectedClinica.nome}
                        </div>
                        <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                          Clínica Escolhida
                        </span>
                      </div>
                      <div className="text-xs text-emerald-800 flex items-start gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                        <span>{selectedClinica.endereco_completo}</span>
                      </div>
                      <div className="text-xs text-emerald-800 flex items-center gap-1.5 pt-1.5 border-t border-emerald-200/60">
                        <Clock className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        <span className="font-bold">Horário de Funcionamento:</span>
                        <span>{horarioFuncionamentoTexto || selectedClinica.horario_funcionamento_texto || 'Segunda a Sexta, das 08:00 às 17:00'}</span>
                      </div>
                    </div>

                    {loadingDatas ? (
                      <div className="py-6 text-center text-xs text-gray-500">
                        <div className="w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                        Consultando agenda da clínica...
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                            Selecione a Data Desejada *
                          </label>
                          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-2">
                            {datasDisponiveis.map((dt) => {
                              const { weekday, dateFormatted } = formatWeekdayAndDate(dt);
                              const isSelected = selectedData === dt;
                              return (
                                <button
                                  key={dt}
                                  type="button"
                                  onClick={() => setSelectedData(dt)}
                                  className={`p-2.5 sm:p-3 rounded-xl border text-xs font-semibold transition-all flex flex-col items-center justify-center gap-0.5 ${
                                    isSelected
                                      ? 'border-emerald-600 bg-emerald-600 text-white shadow-xs'
                                      : 'border-gray-200 bg-white text-gray-700 hover:border-emerald-300 active:bg-gray-50'
                                  }`}
                                >
                                  {weekday && (
                                    <span className="text-[10px] uppercase font-bold opacity-85">{weekday}</span>
                                  )}
                                  <span className="font-bold text-xs sm:text-sm">{dateFormatted}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {possuiHoraMarcada && horariosDisponiveis.length > 0 ? (
                          <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                              Horário Agendado *
                            </label>
                            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                              {horariosDisponiveis.map((hr) => (
                                <button
                                  key={hr}
                                  type="button"
                                  onClick={() => setSelectedHorario(hr)}
                                  className={`p-2.5 rounded-xl border text-xs font-semibold transition-all ${
                                    selectedHorario === hr
                                      ? 'border-emerald-600 bg-emerald-600 text-white shadow-xs'
                                      : 'border-gray-200 bg-white text-gray-700 hover:border-emerald-300'
                                  }`}
                                >
                                  {hr}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="p-3.5 bg-blue-50/80 border border-blue-200/80 rounded-xl flex items-start gap-2.5 text-xs text-blue-950">
                            <Clock className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                            <div>
                              <span className="font-bold block text-blue-900">Atendimento por ordem de chegada</span>
                              <span className="text-blue-700 text-[11px] leading-relaxed">
                                {horarioFuncionamentoTexto || selectedClinica.horario_funcionamento_texto
                                  ? `Horário de funcionamento: ${horarioFuncionamentoTexto || selectedClinica.horario_funcionamento_texto}`
                                  : 'Esta clínica realiza atendimento por ordem de chegada no dia agendado durante o horário de funcionamento.'}
                              </span>
                            </div>
                          </div>
                        )}

                        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div>
                            <div className="text-xs sm:text-sm font-bold text-emerald-950">
                              Atendimento em: {selectedClinica.nome}
                            </div>
                            <div className="text-xs text-emerald-700 mt-0.5">
                              Data prevista: <strong>{formatDateBR(selectedData)}</strong>
                              {selectedHorario ? ` às ${selectedHorario}` : ' (ordem de chegada)'}
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={handleConfirmarAgendamento}
                            disabled={submittingAgendamento || !selectedData}
                            className="w-full sm:w-auto px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs sm:text-sm rounded-xl shadow-md transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            {submittingAgendamento ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Confirmando Agendamento...
                              </>
                            ) : (
                              <>
                                <CheckCircle2 className="w-4 h-4" /> Confirmar Agendamento
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          </div>
        )}

        {/* ABA 1: CASO SEM TOKEN ATIVO */}
        {activeTab === 'agendamento' && !activeToken && (
          <div className="bg-white rounded-3xl border border-gray-200 p-8 sm:p-12 shadow-sm text-center max-w-xl mx-auto space-y-4">
            <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto border border-emerald-100">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">
              Você não possui nenhum agendamento pendente no momento
            </h2>
            <p className="text-xs text-gray-500 max-w-md mx-auto leading-relaxed">
              Para consultar seus exames realizados ou emitir documentos, acesse a aba Meus Exames e Agendamentos.
            </p>
            <div className="pt-2">
              <button
                type="button"
                onClick={() => handleTabChange('exames')}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-sm transition-all"
              >
                Ver Meus Exames e Agendamentos <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* ABA 2: LISTAGEM DE MEUS EXAMES E AGENDAMENTOS */}
        {activeTab === 'exames' && (perfil || autoagendamentoData) && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-gray-900">
                  {perfil?.is_candidato ? 'Seu Exame Médico Admissional' : 'Meus Exames e Agendamentos'}
                </h2>
                <p className="text-xs text-gray-500">
                  {perfil?.is_candidato
                    ? 'Acompanhe as instruções, local e data para a realização do seu exame médico de admissão.'
                    : 'Acompanhe seus exames agendados e concluídos, baixe suas guias, fichas e ASO emitido.'}
                </p>
              </div>
              {(perfil?.id || autoagendamentoData?.colaborador?.id) && (
                <button
                  type="button"
                  onClick={() => {
                    const id = perfil?.id || autoagendamentoData?.colaborador?.id;
                    if (id) loadExames(id);
                  }}
                  className="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-lg text-xs font-semibold transition-colors"
                >
                  Atualizar Lista
                </button>
              )}
            </div>

            {loadingExames ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-xs text-gray-500">Carregando exames do colaborador...</p>
              </div>
            ) : exames.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center space-y-3">
                <Stethoscope className="w-10 h-10 text-gray-300 mx-auto" />
                <h3 className="text-sm font-bold text-gray-700">Nenhum exame localizado</h3>
                <p className="text-xs text-gray-500 max-w-sm mx-auto">
                  Você não possui exames pendentes ou realizados registrados sob este CPF no momento.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {exames.map((ex) => {
                  const hojeStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
                  const isAgendamentoHoje = Boolean(
                    ex.data_agendamento && ex.data_agendamento.split('T')[0] === hojeStr,
                  );

                  return (
                    <div
                      key={ex.id}
                      className="bg-white rounded-2xl border border-gray-200 p-5 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                    >
                      <div className="space-y-1.5 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              ex.status === 'concluido'
                                ? 'bg-emerald-100 text-emerald-800'
                                : ex.status === 'pendente_escolha'
                                ? 'bg-amber-100 text-amber-800 animate-pulse'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {ex.status_label}
                          </span>
                          {ex.confirmou_presenca ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                              Presença Confirmada
                            </span>
                          ) : isAgendamentoHoje && ex.status !== 'cancelado' && ex.status !== 'pendente_escolha' ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-900 border border-amber-300 flex items-center gap-1">
                              <Clock className="w-3 h-3 text-amber-600" />
                              Atendimento Hoje
                            </span>
                          ) : null}
                          <span className="text-xs text-gray-400 font-medium">#{ex.id}</span>
                          {ex.tipo_exame && (
                            <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                              {ex.tipo_exame}
                            </span>
                          )}
                        </div>

                        <h3 className="text-sm font-bold text-gray-900">{ex.exame_nome}</h3>

                        {/* Lista dos procedimentos inclusos neste agendamento */}
                        {ex.procedimentos && ex.procedimentos.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5 py-1">
                            {ex.procedimentos.map((proc, pIdx) => (
                              <span
                                key={pIdx}
                                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-700 border border-slate-200"
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full ${
                                    proc.tipo === 'CLINICO' ? 'bg-primary-600' : 'bg-indigo-500'
                                  }`}
                                />
                                {proc.nome}
                              </span>
                            ))}
                          </div>
                        )}

                        {ex.status === 'pendente_escolha' || !ex.clinica_id || !ex.data_agendamento ? (
                          <div className="flex items-center gap-1.5 text-xs text-amber-700 font-medium">
                            <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                            <span>Pendente de escolha da clínica</span>
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3.5 h-3.5 text-gray-400" />
                              {ex.clinica_nome}
                            </span>
                            {ex.data_agendamento && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5 text-gray-400" />
                                {formatDateBR(ex.data_agendamento)}
                                {ex.horario ? ` às ${ex.horario}` : ''}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Banner Informativo quando Presença já está confirmada */}
                        {ex.confirmou_presenca && (
                          <div className="mt-2.5 p-2.5 bg-emerald-50/80 border border-emerald-200/90 rounded-xl text-xs text-emerald-950 flex flex-col sm:flex-row sm:items-center justify-between gap-2 shadow-2xs">
                            <div className="flex items-start sm:items-center gap-2">
                              <div className="w-6 h-6 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0 mt-0.5 sm:mt-0">
                                <ShieldCheck className="w-3.5 h-3.5" />
                              </div>
                              <div>
                                <span className="font-bold block text-emerald-900 leading-tight">
                                  Presença confirmada no exame
                                </span>
                                <span className="text-[11px] text-emerald-700 block">
                                  {ex.tipo_confirmacao === 'ja_chegou' ? 'Chegada registrada' : 'Previsão informada'} às{' '}
                                  <strong>{ex.presenca_horario || ex.horario || 'hoje'}</strong>
                                  {ex.presenca_em && ` • em ${formatDateTimeBR(ex.presenca_em)}`}
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setSelectedComprovante(ex)}
                              className="self-start sm:self-center px-2.5 py-1 bg-white hover:bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-lg text-xs font-bold transition-colors cursor-pointer flex items-center gap-1 shadow-2xs shrink-0"
                            >
                              <span>Ver Confirmação</span>
                              <ArrowRight className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-2 shrink-0">
                        {/* Botão de Confirmar Presença para exames ativos com clínica e data (estritamente hoje) */}
                        {!ex.confirmou_presenca &&
                          ex.status !== 'cancelado' &&
                          ex.status !== 'pendente_escolha' &&
                          ex.status !== 'concluido' &&
                          Boolean(ex.clinica_id) &&
                          Boolean(ex.data_agendamento) && (
                            isAgendamentoHoje ? (
                              <button
                                type="button"
                                onClick={() => handleIrParaConfirmarPresenca(ex.token_presenca)}
                                className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold text-xs rounded-xl shadow-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                              >
                                <MapPin className="w-3.5 h-3.5" />
                                <span>Confirmar Presença</span>
                              </button>
                            ) : (
                              <span
                                className="px-3 py-1.5 bg-slate-100 text-slate-600 text-xs font-semibold rounded-xl border border-slate-200 flex items-center gap-1.5 shadow-2xs"
                                title={
                                  ex.data_agendamento && ex.data_agendamento.split('T')[0] > hojeStr
                                    ? 'A confirmação de presença com selfie é liberada estritamente no dia do exame'
                                    : 'Data de atendimento expirada'
                                }
                              >
                                <Clock className="w-3.5 h-3.5 text-slate-500" />
                                <span>
                                  {ex.data_agendamento && ex.data_agendamento.split('T')[0] > hojeStr
                                    ? 'Disponível no dia do exame'
                                    : 'Agendamento Expirado'}
                                </span>
                              </span>
                            )
                          )}

                        {/* Botão Ver Comprovante */}
                        {ex.confirmou_presenca && (
                          <button
                            type="button"
                            onClick={() => setSelectedComprovante(ex)}
                            className="px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-bold text-xs rounded-xl border border-emerald-200 transition-colors flex items-center gap-1.5 cursor-pointer"
                            title="Ver Dados de Confirmação de Presença"
                          >
                            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                            <span>Comprovante</span>
                          </button>
                        )}

                        {ex.status === 'pendente_escolha' && ex.token_escolha_clinica && (
                          <button
                            type="button"
                            onClick={() => {
                              setActiveToken(ex.token_escolha_clinica!);
                              setAgendamentoClearedAfterFinish(false);
                              handleTabChange('agendamento');
                            }}
                            className="px-3.5 py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-xl shadow-xs transition-colors flex items-center gap-1.5"
                          >
                            <Calendar className="w-3.5 h-3.5" /> Escolher Clínica
                          </button>
                        )}

                        {/* Não exibir Guia, Kit ou Ficha para agendamento por parte do colaborador enquanto não for escolhida a clínica e data */}
                        {Boolean(ex.clinica_id) &&
                          Boolean(ex.data_agendamento) &&
                          Boolean(
                            ex.clinica_nome &&
                              ex.clinica_nome !== '-' &&
                              !ex.clinica_nome.toLowerCase().includes('definir') &&
                              !ex.clinica_nome.toLowerCase().includes('pendente'),
                          ) && (
                            <>
                              {ex.possui_guia_encaminhamento && (
                                <button
                                  type="button"
                                  onClick={() => handleDownloadGuia(ex.id)}
                                  disabled={downloadingDocId === ex.id}
                                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-xs rounded-xl transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                  title="Baixar Guia de Encaminhamento"
                                >
                                  {downloadingDocId === ex.id ? (
                                    <div className="w-3.5 h-3.5 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <Download className="w-3.5 h-3.5 text-gray-500" />
                                  )}
                                  Guia
                                </button>
                              )}

                              <button
                                type="button"
                                onClick={() => handleDownloadFicha(ex.id)}
                                disabled={downloadingFichaId === ex.id}
                                className="px-3 py-2 bg-white hover:bg-gray-100 text-gray-700 font-bold text-xs rounded-xl border border-gray-200 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                title="Baixar Ficha Clínica do Exame"
                              >
                                {downloadingFichaId === ex.id ? (
                                  <div className="w-3.5 h-3.5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <FileText className="w-3.5 h-3.5 text-gray-500" />
                                )}
                                Ficha
                              </button>
                            </>
                          )}

                        {ex.possui_aso_disponivel ? (
                          <button
                            type="button"
                            onClick={() => handleDownloadASO(ex.id)}
                            disabled={downloadingDocId === ex.id}
                            className="px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-bold text-xs rounded-xl border border-emerald-200 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                            title="Baixar ASO Assinado"
                          >
                            {downloadingDocId === ex.id ? (
                              <div className="w-3.5 h-3.5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <FileText className="w-3.5 h-3.5 text-emerald-600" />
                            )}
                            ASO
                          </button>
                        ) : (
                          <span
                            className="px-2.5 py-1.5 bg-gray-100 text-gray-500 rounded-xl text-[11px] font-medium flex items-center gap-1 border border-gray-200"
                            title="O ASO estará disponível após a realização do exame na clínica e laudo médico emitido."
                          >
                            <Clock className="w-3 h-3 text-gray-400" /> ASO disponível após exame
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Modal de Comprovante / Dados da Confirmação de Presença */}
        {selectedComprovante && (
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 animate-in fade-in zoom-in-95 duration-150 border border-emerald-100">
              <div className="flex items-start justify-between pb-3 border-b border-gray-100">
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                    <ShieldCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 leading-tight">
                      Comprovante de Presença
                    </h3>
                    <span className="text-xs text-gray-500">
                      Confirmação registrada no sistema
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedComprovante(null)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  ✕
                </button>
              </div>

              {/* Informações do Agendamento */}
              <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 text-xs space-y-2">
                <div className="flex items-start justify-between">
                  <span className="text-gray-500 font-semibold">Exame:</span>
                  <span className="font-bold text-gray-900 text-right">{selectedComprovante.exame_nome}</span>
                </div>
                <div className="flex items-start justify-between">
                  <span className="text-gray-500 font-semibold">Data do Agendamento:</span>
                  <span className="font-bold text-gray-900">{formatDateBR(selectedComprovante.data_agendamento || '')}</span>
                </div>
                <div className="flex items-start justify-between">
                  <span className="text-gray-500 font-semibold">Horário Registrado:</span>
                  <span className="font-bold text-emerald-800">
                    {selectedComprovante.presenca_horario || selectedComprovante.horario || 'Informado'}
                    {' '}({selectedComprovante.tipo_confirmacao === 'ja_chegou' ? 'Já na clínica' : 'Horário previsto'})
                  </span>
                </div>
                {selectedComprovante.clinica_nome && (
                  <div className="flex items-start justify-between pt-1 border-t border-slate-200/70">
                    <span className="text-gray-500 font-semibold">Clínica:</span>
                    <span className="font-bold text-gray-900 text-right max-w-[220px]">{selectedComprovante.clinica_nome}</span>
                  </div>
                )}
                {selectedComprovante.presenca_em && (
                  <div className="flex items-start justify-between pt-1 border-t border-slate-200/70">
                    <span className="text-gray-500 font-semibold">Registrado em:</span>
                    <span className="font-medium text-gray-700">{formatDateTimeBR(selectedComprovante.presenca_em)}</span>
                  </div>
                )}
              </div>

              {/* Foto / Selfie de Comprovação */}
              {selectedComprovante.selfie_path ? (
                <div className="text-center pt-1">
                  <label className="block text-xs font-semibold text-gray-700 mb-2">
                    Foto registrada para auditoria:
                  </label>
                  {(() => {
                    const targetIdOrToken =
                      selectedComprovante.token_presenca ||
                      selectedComprovante.token_escolha_clinica ||
                      selectedComprovante.id;
                    const computedSrc =
                      selectedComprovante.selfie_path.startsWith('blob:') ||
                      selectedComprovante.selfie_path.startsWith('data:') ||
                      selectedComprovante.selfie_path.startsWith('http://') ||
                      selectedComprovante.selfie_path.startsWith('https://')
                        ? selectedComprovante.selfie_path
                        : targetIdOrToken
                        ? `${getApiHostURL()}/customer/presenca/${targetIdOrToken}/selfie`
                        : selectedComprovante.selfie_path;

                    return (
                      <img
                        src={computedSrc}
                        onError={(e) => {
                          if (targetIdOrToken) {
                            const fallbackUrl = `${getApiHostURL()}/customer/presenca/${targetIdOrToken}/selfie`;
                            if (!e.currentTarget.src.includes(fallbackUrl)) {
                              e.currentTarget.src = fallbackUrl;
                              return;
                            }
                          }
                          e.currentTarget.style.display = 'none';
                        }}
                        alt="Selfie de Confirmação"
                        className="w-44 h-44 object-cover rounded-2xl mx-auto border-2 border-emerald-400 shadow-md"
                      />
                    );
                  })()}
                  <span className="text-[11px] text-gray-500 mt-1 block">
                    Identidade verificada pelo colaborador
                  </span>
                </div>
              ) : (
                <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200 text-xs text-emerald-800 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Presença registrada com sucesso no sistema SST Exames.</span>
                </div>
              )}

              <div className="pt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedComprovante(null)}
                  className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-xs transition-colors cursor-pointer"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ABA 3: CANAL DE DENÚNCIAS 100% ANÔNIMO (apenas para colaboradores efetivos) */}
        {activeTab === 'denuncia' && !perfil?.is_candidato && (
          <div className="max-w-4xl mx-auto">
            {/* ======================================================== */}
            {/* FLUXO MOBILE DEDICADO: CANAL DE DENÚNCIAS                */}
            {/* ======================================================== */}
            <div className="sm:hidden space-y-3.5 pb-20">
              {/* Header Mobile Compacto */}
              <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-xs space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center border border-emerald-200/60">
                      <ShieldAlert className="w-4 h-4" />
                    </span>
                    <div>
                      <h2 className="text-sm font-bold text-gray-900 leading-tight">Canal de Denúncias</h2>
                      <span className="text-[10px] text-gray-400 font-medium">Relatos Éticos Confidenciais</span>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold text-emerald-800 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200 flex items-center gap-1">
                    <Lock className="w-3 h-3 text-emerald-600" /> 100% Anônimo
                  </span>
                </div>

                {/* Banner de Anonimato Retrátil */}
                <div className="bg-emerald-50/70 border border-emerald-200/70 rounded-xl p-3 text-xs text-emerald-950">
                  <div
                    onClick={() => setMobileDenunciaInfoOpen(!mobileDenunciaInfoOpen)}
                    className="flex items-center justify-between cursor-pointer"
                  >
                    <span className="font-bold flex items-center gap-1.5 text-xs text-emerald-900">
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                      Garantia de Sigilo Absoluto
                    </span>
                    <button
                      type="button"
                      className="text-[11px] font-bold text-emerald-700 flex items-center gap-0.5"
                    >
                      {mobileDenunciaInfoOpen ? 'Ocultar' : 'Como funciona?'}
                      <ChevronDown
                        className={`w-3.5 h-3.5 transition-transform ${mobileDenunciaInfoOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                  </div>
                  {mobileDenunciaInfoOpen && (
                    <p className="text-[11px] text-emerald-800 mt-2 leading-relaxed pt-2 border-t border-emerald-200/60">
                      Este canal não registra seu endereço IP, dispositivo ou qualquer dado de navegação. Ao enviar, você recebe um código de protocolo único para acompanhar a apuração pelo Comitê de Ética.
                    </p>
                  )}
                </div>

                {/* Segmented Control iOS para Sub-abas */}
                <div className="bg-gray-100/90 p-1 rounded-xl flex gap-1">
                  <button
                    type="button"
                    onClick={() => setDenunciaSubTab('nova')}
                    className={`flex-1 py-2.5 px-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
                      denunciaSubTab === 'nova'
                        ? 'bg-white text-emerald-800 shadow-xs ring-1 ring-black/5'
                        : 'text-gray-600 active:bg-gray-200/60'
                    }`}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    Nova Denúncia
                  </button>
                  <button
                    type="button"
                    onClick={() => setDenunciaSubTab('consultar')}
                    className={`flex-1 py-2.5 px-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
                      denunciaSubTab === 'consultar'
                        ? 'bg-white text-emerald-800 shadow-xs ring-1 ring-black/5'
                        : 'text-gray-600 active:bg-gray-200/60'
                    }`}
                  >
                    <Search className="w-3.5 h-3.5" />
                    Consultar Protocolo
                  </button>
                </div>
              </div>

              {/* Sub-aba 1: Nova Denúncia (Mobile) */}
              {denunciaSubTab === 'nova' && (
                <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-xs">
                  {denunciaProtocoloGerado ? (
                    <div className="text-center space-y-4 py-2">
                      <div className="w-12 h-12 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center mx-auto">
                        <CheckCircle2 className="w-6 h-6" />
                      </div>

                      <div>
                        <h3 className="text-base font-bold text-gray-900">Relato Enviado com Sucesso!</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Sua mensagem foi enviada ao Comitê de Ética sob sigilo.
                        </p>
                      </div>

                      <div className="p-3.5 bg-gray-50 border border-gray-200 rounded-xl space-y-2 text-left">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">
                          Seu Protocolo de Acompanhamento:
                        </span>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-base font-mono font-black text-emerald-900 tracking-wide">
                            {denunciaProtocoloGerado}
                          </span>
                          <button
                            type="button"
                            onClick={async () => {
                              const ok = await copyToClipboard(denunciaProtocoloGerado);
                              if (ok) {
                                setCopiedProtocolo(true);
                                setTimeout(() => setCopiedProtocolo(false), 2000);
                              }
                            }}
                            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-xs font-bold rounded-lg shadow-xs transition-all flex items-center gap-1"
                          >
                            {copiedProtocolo ? (
                              <>
                                <CheckCircle2 className="w-3.5 h-3.5" /> Copiado!
                              </>
                            ) : (
                              <>
                                <Copy className="w-3.5 h-3.5" /> Copiar
                              </>
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 text-left flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <span>
                          <strong>Guarde este código:</strong> por garantia de sigilo, não é possível recuperar o protocolo se for perdido.
                        </span>
                      </div>

                      <div className="space-y-2 pt-2">
                        <button
                          type="button"
                          onClick={() => {
                            setConsultaProtocoloInput(denunciaProtocoloGerado);
                            setDenunciaSubTab('consultar');
                            setDenunciaProtocoloGerado(null);
                          }}
                          className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-1.5"
                        >
                          <Search className="w-3.5 h-3.5" /> Acompanhar Este Protocolo Agora
                        </button>
                        <button
                          type="button"
                          onClick={() => setDenunciaProtocoloGerado(null)}
                          className="w-full py-2.5 bg-white text-gray-700 hover:bg-gray-50 font-bold text-xs rounded-xl border border-gray-200 transition-colors"
                        >
                          Registrar Novo Relato
                        </button>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleEnviarDenuncia} className="space-y-4">
                      {/* Categoria do Relato */}
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1">
                          Categoria do Relato <span className="text-rose-500">*</span>
                        </label>
                        <select
                          value={denunciaForm.tipo}
                          onChange={(e) =>
                            setDenunciaForm({
                              ...denunciaForm,
                              tipo: e.target.value as CriarDenunciaAnonimaPayloadContract['tipo'],
                            })
                          }
                          className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-xl text-xs text-gray-900 bg-white shadow-2xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        >
                          <option value="assedio_moral">Assédio Moral</option>
                          <option value="assedio_sexual">Assédio Sexual</option>
                          <option value="seguranca_trabalho">Segurança do Trabalho / Risco Grave</option>
                          <option value="discriminacao">Discriminação / Preconceito</option>
                          <option value="fraude">Fraude / Desvio de Conduta</option>
                          <option value="outro">Outro Motivo</option>
                        </select>
                      </div>

                      {/* Setor ou Local */}
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1">
                          Setor ou Local do Ocorrido <span className="text-gray-400 font-normal">(opcional)</span>
                        </label>
                        <input
                          type="text"
                          placeholder="Ex: Almoxarifado, Sala de Reunião, Produção..."
                          value={denunciaForm.setor_local || ''}
                          onChange={(e) => setDenunciaForm({ ...denunciaForm, setor_local: e.target.value })}
                          className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-xl text-xs text-gray-900 bg-white shadow-2xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 placeholder-gray-400"
                        />
                      </div>

                      {/* Data Aproximada */}
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1">
                          Data Aproximada do Ocorrido <span className="text-gray-400 font-normal">(opcional)</span>
                        </label>
                        <input
                          type="date"
                          value={denunciaForm.data_hora_fato || ''}
                          onChange={(e) => setDenunciaForm({ ...denunciaForm, data_hora_fato: e.target.value })}
                          className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-xl text-xs text-gray-900 bg-white shadow-2xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                      </div>

                      {/* Envolvidos */}
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1">
                          Pessoas ou Cargos Envolvidos <span className="text-gray-400 font-normal">(opcional)</span>
                        </label>
                        <input
                          type="text"
                          placeholder="Ex: Gestor do setor, colega de turno..."
                          value={denunciaForm.envolvidos || ''}
                          onChange={(e) => setDenunciaForm({ ...denunciaForm, envolvidos: e.target.value })}
                          className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-xl text-xs text-gray-900 bg-white shadow-2xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 placeholder-gray-400"
                        />
                      </div>

                      {/* Relato */}
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1">
                          Descrição Detalhada do Relato <span className="text-rose-500">*</span>
                        </label>
                        <textarea
                          required
                          rows={4}
                          placeholder="Descreva o que ocorreu de forma clara e detalhada. Não inclua seus dados se desejar anonimato."
                          value={denunciaForm.relato}
                          onChange={(e) => setDenunciaForm({ ...denunciaForm, relato: e.target.value })}
                          className="w-full p-3 border border-gray-300 rounded-xl text-xs text-gray-900 bg-white shadow-2xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 placeholder-gray-400 resize-y"
                        />
                      </div>

                      {/* Botões de Ação Mobile */}
                      <div className="pt-2 space-y-2">
                        <button
                          type="submit"
                          disabled={submittingDenuncia}
                          className="w-full py-3.5 px-4 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          {submittingDenuncia ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Enviando com Sigilo...
                            </>
                          ) : (
                            <>
                              <ShieldCheck className="w-4 h-4" />
                              Enviar Denúncia Anônima
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setDenunciaForm({
                              tipo: 'assedio_moral',
                              setor_local: '',
                              data_hora_fato: '',
                              envolvidos: '',
                              relato: '',
                            })
                          }
                          className="w-full py-2 text-xs font-semibold text-gray-500 hover:text-gray-700 underline text-center"
                        >
                          Limpar Formulário
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {/* Sub-aba 2: Consultar Protocolo (Mobile) */}
              {denunciaSubTab === 'consultar' && (
                <div className="space-y-4">
                  <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-xs space-y-3">
                    <div>
                      <h3 className="text-xs font-bold text-gray-900">Consultar Protocolo</h3>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        Insira o código do protocolo recebido no envio do relato.
                      </p>
                    </div>

                    <form onSubmit={handleConsultarProtocolo} className="space-y-2.5">
                      <div className="relative">
                        <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          required
                          placeholder="Ex: DEN-2026-ABC123"
                          value={consultaProtocoloInput}
                          onChange={(e) => setConsultaProtocoloInput(e.target.value)}
                          className="w-full min-h-[44px] pl-10 pr-3 py-2 border border-gray-300 rounded-xl text-xs font-mono uppercase text-gray-900 bg-white shadow-2xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 placeholder-gray-400"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={consultingDenuncia}
                        className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-1.5 disabled:opacity-50"
                      >
                        {consultingDenuncia ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                        Consultar Status
                      </button>
                    </form>

                    {consultaError && (
                      <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-rose-500 shrink-0" />
                        {consultaError}
                      </div>
                    )}
                  </div>

                  {denunciaConsultada && (
                    <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-xs space-y-3">
                      <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                        <div>
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">
                            Protocolo
                          </span>
                          <strong className="text-sm font-mono font-bold text-gray-900">
                            {denunciaConsultada.protocolo}
                          </strong>
                        </div>
                        <span
                          className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${
                            denunciaConsultada.status === 'concluida'
                              ? 'bg-emerald-100 text-emerald-800'
                              : denunciaConsultada.status === 'em_analise'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {denunciaConsultada.status_label}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs bg-gray-50 p-3 rounded-xl border border-gray-100">
                        <div>
                          <span className="text-[10px] text-gray-400 font-bold block">Categoria:</span>
                          <strong className="text-gray-800 text-xs">{denunciaConsultada.tipo_label}</strong>
                        </div>
                        <div>
                          <span className="text-[10px] text-gray-400 font-bold block">Data:</span>
                          <strong className="text-gray-800 text-xs">
                            {formatDateBR(denunciaConsultada.created_at)}
                          </strong>
                        </div>
                      </div>

                      <div className="pt-2 border-t border-gray-100">
                        <span className="text-xs font-bold text-gray-800 block mb-1.5">
                          Parecer da Comissão de Ética:
                        </span>
                        {denunciaConsultada.resposta_empresa ? (
                          <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-700 leading-relaxed">
                            <p>{denunciaConsultada.resposta_empresa}</p>
                          </div>
                        ) : (
                          <div className="p-3 bg-amber-50/70 border border-dashed border-amber-200 rounded-xl text-xs text-amber-800 flex items-center gap-2">
                            <Clock className="w-4 h-4 text-amber-600 shrink-0" />
                            <span>Apuração em andamento sob sigilo profissional.</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ======================================================== */}
            {/* FLUXO DESKTOP: MANTIDO 100% IDÊNTICO E INTACTO          */}
            {/* ======================================================== */}
            <div className="hidden sm:block space-y-6">
              {/* Cabeçalho Padrão da Aplicação */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-4 border-b border-gray-200">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <ShieldAlert className="w-6 h-6 text-emerald-600" />
                  Canal de Denúncias e Relatos Éticos
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Ambiente corporativo seguro para comunicação confidencial de condutas inadequadas ou desvios éticos.
                </p>
              </div>
            </div>

            {/* Banner Informativo de Confidencialidade e Sigilo */}
            <div className="bg-emerald-50/80 border border-emerald-200 rounded-lg p-4 flex items-start gap-3.5">
              <Lock className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div className="text-sm">
                <strong className="font-semibold text-emerald-950 block">Garantia de Anonimato e Segurança</strong>
                <p className="text-xs text-emerald-800 mt-0.5 leading-relaxed">
                  Este é um canal independente e 100% anônimo. Não registramos endereço IP, dados de conexão ou qualquer informação pessoal. Ao concluir o envio, você receberá um <strong>código de protocolo</strong> para acompanhar o parecer da comissão de ética com total sigilo.
                </p>
              </div>
            </div>

            {/* Navegação por Sub-abas (Padrão TabNavigation) */}
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8" aria-label="Abas de Denúncia">
                <button
                  type="button"
                  onClick={() => setDenunciaSubTab('nova')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors flex items-center gap-2 ${
                    denunciaSubTab === 'nova'
                      ? 'border-emerald-600 text-emerald-700 font-semibold'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <FileText className="w-4 h-4" />
                  Registrar Nova Denúncia
                </button>
                <button
                  type="button"
                  onClick={() => setDenunciaSubTab('consultar')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors flex items-center gap-2 ${
                    denunciaSubTab === 'consultar'
                      ? 'border-emerald-600 text-emerald-700 font-semibold'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Search className="w-4 h-4" />
                  Consultar Protocolo Existente
                </button>
              </nav>
            </div>

            {/* Sub-aba 1: Nova Denúncia */}
            {denunciaSubTab === 'nova' && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-xs p-6">
                {denunciaProtocoloGerado ? (
                  <div className="max-w-md mx-auto text-center space-y-6 py-4">
                    <div className="w-12 h-12 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center mx-auto">
                      <CheckCircle2 className="w-6 h-6" />
                    </div>

                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Relato Registrado com Sucesso</h3>
                      <p className="text-xs text-gray-600 mt-1">
                        Sua mensagem foi enviada ao Comitê de Ética. Salve o número do protocolo para consultar futuras respostas.
                      </p>
                    </div>

                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between gap-3 text-left">
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">
                          Protocolo de Acompanhamento
                        </span>
                        <span className="text-base font-mono font-bold text-emerald-800">
                          {denunciaProtocoloGerado}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          const ok = await copyToClipboard(denunciaProtocoloGerado);
                          if (ok) {
                            setCopiedProtocolo(true);
                            setTimeout(() => setCopiedProtocolo(false), 2000);
                          }
                        }}
                        className="px-3 py-1.5 bg-white hover:bg-gray-100 text-gray-700 text-xs font-medium rounded-lg border border-gray-300 shadow-2xs transition-colors flex items-center gap-1.5"
                      >
                        {copiedProtocolo ? (
                          <>
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                            Copiado!
                          </>
                        ) : (
                          <>
                            <Copy className="w-3.5 h-3.5 text-gray-500" />
                            Copiar
                          </>
                        )}
                      </button>
                    </div>

                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 text-left flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <span>
                        Importante: por razões de anonimato absoluto, não é possível recuperar este protocolo se for perdido. Guarde-o em local seguro.
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => {
                          setConsultaProtocoloInput(denunciaProtocoloGerado);
                          setDenunciaSubTab('consultar');
                          setDenunciaProtocoloGerado(null);
                        }}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs rounded-lg transition-colors flex items-center gap-1.5 shadow-xs"
                      >
                        <Search className="w-3.5 h-3.5" /> Consultar Este Protocolo
                      </button>
                      <button
                        type="button"
                        onClick={() => setDenunciaProtocoloGerado(null)}
                        className="px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 font-medium text-xs rounded-lg border border-gray-300 transition-colors"
                      >
                        Registrar Novo Relato
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleEnviarDenuncia} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Categoria do Relato <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={denunciaForm.tipo}
                          onChange={(e) =>
                            setDenunciaForm({
                              ...denunciaForm,
                              tipo: e.target.value as CriarDenunciaAnonimaPayloadContract['tipo'],
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white shadow-xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                        >
                          <option value="assedio_moral">Assédio Moral</option>
                          <option value="assedio_sexual">Assédio Sexual</option>
                          <option value="seguranca_trabalho">Segurança do Trabalho / Risco Grave</option>
                          <option value="discriminacao">Discriminação / Preconceito</option>
                          <option value="fraude">Fraude / Desvio de Conduta</option>
                          <option value="outro">Outro Motivo</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Setor ou Local do Ocorrido
                        </label>
                        <input
                          type="text"
                          placeholder="Ex: Almoxarifado, Sala de Reunião, Produção..."
                          value={denunciaForm.setor_local || ''}
                          onChange={(e) => setDenunciaForm({ ...denunciaForm, setor_local: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white shadow-xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors placeholder-gray-400"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Data Aproximada do Ocorrido
                        </label>
                        <input
                          type="date"
                          value={denunciaForm.data_hora_fato || ''}
                          onChange={(e) => setDenunciaForm({ ...denunciaForm, data_hora_fato: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white shadow-xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Pessoas ou Cargos Envolvidos <span className="text-gray-400 font-normal text-xs">(opcional)</span>
                        </label>
                        <input
                          type="text"
                          placeholder="Ex: Gestor do setor, colega de turno..."
                          value={denunciaForm.envolvidos || ''}
                          onChange={(e) => setDenunciaForm({ ...denunciaForm, envolvidos: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white shadow-xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors placeholder-gray-400"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Descrição Detalhada do Relato <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        required
                        rows={5}
                        placeholder="Descreva o que ocorreu de forma clara e detalhada. Não inclua dados pessoais se desejar permanecer 100% anônimo."
                        value={denunciaForm.relato}
                        onChange={(e) => setDenunciaForm({ ...denunciaForm, relato: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white shadow-xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors placeholder-gray-400 resize-y"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        Informações com data, local e narrativa clara facilitam a apuração e resolução do fato.
                      </p>
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
                      <button
                        type="button"
                        onClick={() =>
                          setDenunciaForm({
                            tipo: 'assedio_moral',
                            setor_local: '',
                            data_hora_fato: '',
                            envolvidos: '',
                            relato: '',
                          })
                        }
                        className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg text-xs font-medium transition-colors"
                      >
                        Limpar Formulário
                      </button>
                      <button
                        type="submit"
                        disabled={submittingDenuncia}
                        className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs rounded-lg shadow-xs transition-colors flex items-center gap-2 disabled:opacity-50"
                      >
                        {submittingDenuncia ? (
                          <>
                            <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Enviando com Sigilo...
                          </>
                        ) : (
                          <>
                            <ShieldCheck className="w-4 h-4" />
                            Enviar Denúncia Anônima
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {/* Sub-aba 2: Consultar Protocolo */}
            {denunciaSubTab === 'consultar' && (
              <div className="space-y-5">
                <div className="bg-white rounded-lg border border-gray-200 shadow-xs p-6 space-y-4">
                  <div>
                    <h3 className="text-base font-bold text-gray-900">Acompanhar Status da Apuração</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Insira o código do protocolo fornecido no momento do envio do relato.
                    </p>
                  </div>

                  <form onSubmit={handleConsultarProtocolo} className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                      <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        required
                        placeholder="Ex: DEN-2026-ABC123"
                        value={consultaProtocoloInput}
                        onChange={(e) => setConsultaProtocoloInput(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 font-mono uppercase shadow-xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors placeholder-gray-400"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={consultingDenuncia}
                      className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5 shadow-xs disabled:opacity-50"
                    >
                      {consultingDenuncia ? (
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Search className="w-3.5 h-3.5" />
                      )}
                      Consultar Protocolo
                    </button>
                  </form>

                  {consultaError && (
                    <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-700 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-rose-500 shrink-0" />
                      {consultaError}
                    </div>
                  )}
                </div>

                {denunciaConsultada && (
                  <div className="bg-white rounded-lg border border-gray-200 shadow-xs p-6 space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-gray-100">
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">
                          Protocolo
                        </span>
                        <strong className="text-base font-mono font-bold text-gray-900">
                          {denunciaConsultada.protocolo}
                        </strong>
                      </div>
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                          denunciaConsultada.status === 'concluida'
                            ? 'bg-emerald-100 text-emerald-800'
                            : denunciaConsultada.status === 'em_analise'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {denunciaConsultada.status_label}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="text-gray-400 block font-medium">Categoria da Ocorrência:</span>
                        <strong className="text-gray-800 text-sm">{denunciaConsultada.tipo_label}</strong>
                      </div>
                      <div>
                        <span className="text-gray-400 block font-medium">Data do Registro:</span>
                        <strong className="text-gray-800 text-sm">
                          {formatDateBR(denunciaConsultada.created_at)}
                        </strong>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-gray-100">
                      <span className="text-xs font-bold text-gray-700 block mb-1.5">
                        Parecer da Comissão de Ética:
                      </span>
                      {denunciaConsultada.resposta_empresa ? (
                        <div className="p-3.5 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700 leading-relaxed">
                          <p>{denunciaConsultada.resposta_empresa}</p>
                        </div>
                      ) : (
                        <div className="p-3.5 bg-gray-50 border border-dashed border-gray-200 rounded-lg text-xs text-gray-500 italic flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          A apuração deste relato está em andamento sob sigilo profissional. Novas atualizações serão publicadas neste protocolo.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            </div>
          </div>
        )}

        {/* ABA 4: MÓDULO CIPA & COMISSÕES (NR-5) - VOTAÇÃO ELETRÔNICA E CANDIDATURAS */}
        {activeTab === 'cipa' && !perfil?.is_candidato && empresaHasCipa && (
          <CipaColaboradorTab perfil={perfil} />
        )}

        {/* ABA 5: MINHA SAÚDE & BEM-ESTAR OCUPACIONAL */}
        {activeTab === 'minha_saude' && perfil && empresaHasSaude && (
          <MinhaSaudeColaboradorTab
            colaboradorId={perfil.id}
            empresaId={perfil.empresa?.id || 0}
            nomeColaborador={perfil.nome}
          />
        )}

      </main>
    </div>
  );
}
